import type {
  ActiveFireFeature,
  AircraftFeature,
  CameraFeature,
  CapAlertDetail,
  CapAlertFeature,
  HighRiskHotspotDetail,
  HighRiskHotspotFeature,
  RawHotspotFeature,
  CameraSmokeFeature,
  UtilityOutageFeature,
  RoadClosureFeature,
  ArcgisFeature,
  OperatorEvacOrderFeature,
  CommsDegradationZoneFeature,
  IncidentType,
  IncidentSourceRecordType,
  Incident,
  IncidentDetail,
  NearbyAircraft,
  NearbyAsset,
  RecommendationType,
  DispatchRecommendation,
  ResponseAssetFeature,
  SensorFeature,
} from '@tsr/shared-types';
import { useCallback, useEffect, useState } from 'react';
import { OBSERVATION_TYPES, type ObservationTypeKey } from '@tsr/shared-types';
import { classifyCapLifeSafety } from '@tsr/shared-types';
import { isEscalationFresh } from '@tsr/shared-types';
import { accessTypeLabel } from '@tsr/shared-types';
import { COVERAGE_LIVE, COVERAGE_SNAPSHOT } from '@tsr/ui';
import { classifyWallMedia, type WallMediaProps } from './wallMediaLabel';
import type {
  VesselFeature,
  DroneCoverageFeature,
  DroneMissionView,
  SmokeAnomalyFeature,
  IceStormAnomalyFeature,
  FloodAnomalyFeature,
  MediaSignalAnomalyFeature,
  HydrometricGaugeFeature,
  CommunityFeature,
} from './api';
import { CameraMedia } from './CameraMedia';
import { DroneMissionDetailBody } from './DroneMissionDetailBody';
import { DroneMissionThread } from './DroneMissionThread';
import { CommunityBody } from './CommunityBody';
import { CommunityCommsThread } from './CommunityCommsThread';
import { FlyToIncidentButton } from './FlyToIncidentButton';
import { FlythroughButton } from './FlythroughButton';
import { fireSourceLabel, fireSourcesLabel } from './fireSourceLabels';
import { useFeedHealth } from './lib/useFeedHealth';
import { humanizeFeedDownAge } from './FeedDownChip';
import { EvacuationBody, hasEvacuationEvidence } from './EvacuationBody';
import { SectorBody } from './SectorBody';
import { AssetMedia } from './AssetMedia';
import { AircraftMedia } from './AircraftMedia';
import { MarineVesselBody } from './MarineVesselBody';
import { DroneCoverageBody } from './DroneCoverageBody';
import { RequestLiveEyes } from './RequestLiveEyes';
import { RequestAirSupport } from './RequestAirSupport';
import { IncidentDronesPanel } from './IncidentDronesPanel';
import { capToQueueItem, hotspotToQueueItem } from './anomalyQueue';
import { confirmHazardNoun } from './dispatchLabels';
import { AnomalyAckButton } from './AnomalyAckButton';
import { SmokeAnomalyBody } from './SmokeAnomalyBody';
import { IceStormAnomalyBody } from './IceStormAnomalyBody';
import { FloodAnomalyBody } from './FloodAnomalyBody';
import { MediaSignalAnomalyBody } from './MediaSignalAnomalyBody';
import { HydrometricGaugeBody } from './HydrometricGaugeBody';
import { RawHotspotBody } from './RawHotspotBody';
import { CameraSmokeBody } from './CameraSmokeBody';
import { UtilityOutageBody } from './UtilityOutageBody';
import { RoadClosureBody } from './RoadClosureBody';
import { EvacuationOrderBody } from './EvacuationOrderBody';
import { LinkToIncidentControl } from './LinkToIncidentControl';
import { OperatorEvacOrderBody } from './OperatorEvacOrderBody';
import { ArcgisFeatureBody } from './ArcgisFeatureBody';
import { CommsDegradationZoneBody } from './CommsDegradationZoneBody';
import { ConnectedAssistLink } from './ConnectedAssistLink';
import type { AlertSelectionKind } from './lib/alertAssistLink';
import { DispatchCapacityPanel, isDispatchAsset } from './DispatchCapacityPanel';
import { fireStageStyle, responseTypeLabel, upstreamStageGloss } from './styles/fireStages';
import { lifecycleStateStyle } from './styles/lifecycleStates';
import { ResponseLevelBadge } from './ResponseLevelBadge';
import { LifecycleStepper, type LifecycleStep } from './components/LifecycleStepper';
import { IncidentManagementSection } from './components/IncidentManagementSection';
import { OwnershipCoordinationBlock } from './components/OwnershipCoordinationBlock';
import { InteropsThreadPanel } from './InteropsThreadPanel';
import { DestinationPicker } from './DestinationPicker';
import { AgencyPicker } from './AgencyPicker';
import { AgencyTaskDetailBody } from './AgencyTaskDetailBody';
import {
  advanceIncidentLifecycle,
  createDispatchRecommendation,
  createIncident,
  fetchIncidents,
  forwardDispatchRecommendation,
  taskAgencyFromDispatch,
  fetchIncidentForSource,
  fetchAircraftDetail,
  fetchCameras,
  fetchCapAlertDetail,
  fetchHighRiskHotspotDetail,
  fetchNearbyAircraft,
  fetchNearbyAssets,
  fetchResponseAsset,
  fetchResourceRequests,
  transitionResourceRequest,
  fetchStagingAreas,
  createStagingArea,
  closeStagingArea,
} from './api';
import type { AgencyTaskView, ResourceRequestListItem, StagingArea, Sector } from '@tsr/shared-types';
import { getSessionId, logAction } from './lib/operatorActions';
import { showToast } from './lib/toast';
import {
  OWN_INCIDENT_LABEL,
  OWN_INCIDENT_BUSY_LABEL,
  OWN_INCIDENT_ARIA_LABEL,
  OWN_INCIDENT_HELP,
  OWN_INCIDENT_ERROR,
  ownIncidentToast,
  reopenInsteadPrompt,
  REOPEN_NUDGE_HELP,
  REACTIVATE_INCIDENT_LABEL,
  REACTIVATION_STALENESS_WARNING_DAYS,
  staleReactivationWarning,
} from './incidentVocabulary';
import {
  clearWallEscalation,
  escalateToWall,
  useWallEscalation,
  type WallTarget,
} from './selectionBridge';
import { selectionToWallTarget } from './wallEscalate';

export type Selection =
  | { kind: 'camera'; feature: CameraFeature }
  // #195 — a co-located camera cluster the operator clicked; the rail shows a
  // "N cameras here → pick one" list (zoom can't separate them on the map).
  | { kind: 'cameraList'; cameras: CameraFeature[] }
  // #415 (Phase 6b) — a co-located stack of ACT incidents the operator clicked; the
  // rail shows a "N incidents here → pick one" list. The incident sibling of
  // cameraList: Cesium `point` markers can't be screen-fanned apart, so co-located
  // incidents are revealed by a "+N" map badge + this disambiguation list instead.
  | { kind: 'incidentList'; incidents: ColocatedIncidentRef[] }
  | { kind: 'sensor'; feature: SensorFeature }
  | { kind: 'asset'; feature: ResponseAssetFeature }
  | { kind: 'aircraft'; feature: AircraftFeature }
  | { kind: 'fire'; feature: ActiveFireFeature }
  | { kind: 'highRiskHotspot'; feature: HighRiskHotspotFeature }
  | { kind: 'rawHotspot'; feature: RawHotspotFeature }
  | { kind: 'cameraSmoke'; feature: CameraSmokeFeature }
  | { kind: 'utilityOutage'; feature: UtilityOutageFeature }
  | { kind: 'roadClosure'; feature: RoadClosureFeature }
  | { kind: 'evacuationOrder'; feature: ArcgisFeature }
  | { kind: 'operatorEvacOrder'; feature: OperatorEvacOrderFeature }
  | { kind: 'arcgisFeature'; feature: ArcgisFeature }
  | { kind: 'commsDegradationZone'; feature: CommsDegradationZoneFeature }
  | { kind: 'capAlert'; feature: CapAlertFeature }
  | { kind: 'marineVessel'; feature: VesselFeature }
  | { kind: 'droneCoverage'; feature: DroneCoverageFeature }
  // #322 — a drone mission opened from the missions panel; the lifecycle + live feed panel.
  | { kind: 'mission'; mission: DroneMissionView }
  | { kind: 'smokeDetectionAnomaly'; feature: SmokeAnomalyFeature }
  | { kind: 'iceStormAnomaly'; feature: IceStormAnomalyFeature }
  | { kind: 'floodAnomaly'; feature: FloodAnomalyFeature }
  | { kind: 'mediaSignalAnomaly'; feature: MediaSignalAnomalyFeature }
  | { kind: 'hydrometricGauge'; feature: HydrometricGaugeFeature }
  | { kind: 'community'; feature: CommunityFeature }
  | { kind: 'stagingArea'; area: StagingArea }
  | { kind: 'sector'; sector: Sector }
  | { kind: 'evacuation'; incidentId: string }
  // #387 — a tracked incident opened from the Coordination → Incidents worklist.
  // Coordination re-arch Phase 1 (IMS Ruling 1): the detail renders the FULL work-
  // surface (not just the SITREP chain), bound to the incident's SOURCE alert via the
  // provenance carried here. sourceRecord* are null for a manually-declared incident
  // (incident-level actions only).
  | {
      kind: 'incident';
      incidentId: string;
      lat: number;
      lon: number;
      sourceRecordType: IncidentSourceRecordType | null;
      sourceRecordId: string | null;
    }
  // #379 — an agency task opened from a Coordination worklist. viewerRole carries which
  // side opened it: 'requester' (the Tasks/outbound worklist — Cancel) or 'target' (the
  // Inbox worklist — acknowledge/accept/decline/start/complete). Phase 3b-2a/3b-2b.
  | { kind: 'agencyTask'; task: AgencyTaskView; viewerRole: 'requester' | 'target' }
  | null;

/**
 * #415 (Phase 6b) — one incident in a co-located stack. EXACTLY the subset of
 * {@link Selection} an incident click can resolve to: the seven act-incident
 * families MapView's `incidentRefOf` recognises (hr & fc both open the high-risk
 * panel; cap/sm/ice/fl/ou each open their own). Every ref IS a valid Selection, so the
 * disambiguation list
 * dispatches a pick with `setSelected(ref)` — reusing each incident's EXISTING body,
 * zero new dispatch.
 */
export type ColocatedIncidentRef =
  | { kind: 'highRiskHotspot'; feature: HighRiskHotspotFeature }
  | { kind: 'capAlert'; feature: CapAlertFeature }
  | { kind: 'smokeDetectionAnomaly'; feature: SmokeAnomalyFeature }
  | { kind: 'iceStormAnomaly'; feature: IceStormAnomalyFeature }
  | { kind: 'floodAnomaly'; feature: FloodAnomalyFeature }
  | { kind: 'utilityOutage'; feature: UtilityOutageFeature };

interface SidePanelProps {
  selected: Selection;
  loadingCameras: boolean;
  loadingSensors: boolean;
  /** #196 — whether a camera id is pinned to the operations panel (drives the
   *  rail's Pin/Pinned control). */
  isCameraPinned?: (id: string) => boolean;
  /** #196 — pin/unpin a camera (the pin action originates on the COP/rail). */
  onTogglePinCamera?: (c: CameraFeature) => void;
  /** #178/#262 — flip the per-operator viewshed-on-select pref (the camera
   *  panel's "hide viewshed" affordance; persists via operator prefs). */
  onViewshedOnSelectChange?: (on: boolean) => void;
  /** #178/#262 — whether viewshed-on-select is ON (founder-ruled default). */
  viewshedOnSelect?: boolean;
  /** Open a nearby camera from the high-risk-hotspot panel (E.2.3). */
  onSelectCamera?: (c: CameraFeature) => void;
  /** #415 — open ONE incident from a co-located incident list (the incidentList rail). */
  onSelectIncident?: (ref: ColocatedIncidentRef) => void;
  /** Open a nearby response asset from the click-fire panel (R.3.2). */
  onSelectAsset?: (a: ResponseAssetFeature) => void;
  /** Open a nearby live aircraft from the click-fire panel (R.3.3). */
  onSelectAircraft?: (a: AircraftFeature) => void;
  /** Current incident context (last-selected incident) — drives the Drone Coverage
   *  ETA-to-on-station (L.2.2 §3B.6). */
  incidentPoint?: { lat: number; lon: number } | null;
  /** Coordination re-arch Phase 2 (Drone R3) — the working incident's id, persisted
   *  across the drone-pin click, so a drone tasked from the Drone Coverage panel
   *  links to THIS incident instead of the hardcoded null. */
  incidentContextId?: string | null;
  /** #322 — open a drone mission's lifecycle + live-feed panel (from the missions panel row). */
  onSelectMission?: (m: DroneMissionView) => void;
  /** Called after a smoke_detected confirm/dismiss so the parent refreshes the
   *  queue + clears the selection (L.2.2 §6.2). */
  onSmokeResolved?: (outcome: 'confirmed' | 'dismissed', promotedHotspotId?: string) => void;
  /** Called after an ice-storm anomaly acknowledge/dismiss so the parent refreshes
   *  the queue + clears the selection (§4.5). */
  onIceResolved?: (outcome: 'acknowledged' | 'dismissed') => void;
  /** Called after a flood anomaly acknowledge/dismiss so the parent refreshes the
   *  queue + clears the selection (§4.4). */
  onFloodResolved?: (outcome: 'acknowledged' | 'dismissed') => void;
  /** #184 — called after a media-signal spike confirm/dismiss so the parent
   *  refreshes the queue (and clears the selection on dismiss). */
  onMediaSignalResolved?: (outcome: 'confirmed' | 'dismissed') => void;
  /** W.2 — called with a community id after a headcount/wellness edit so the
   *  parent recolours the community map marker (status cache). */
  onCommunityChanged?: (id: string) => void;
  /** W.2 editor — start the place-a-point flow to re-place a community's centroid. */
  onMoveCommunity?: (community: CommunityFeature) => void;
  /** #445-(3) — start the draw→declare flow for an operator evac order, pre-seeded
   *  with the community as the named issuing authority. */
  onRecordEvacOrder?: (preseed: {
    communityId: string;
    communityName: string;
    communityType: string;
  }) => void;
  /** #445-(3) — called after an escalate/rescind/correct so the parent refreshes the
   *  layer + reflects the updated order. */
  onOperatorEvacOrderChanged?: (updated: OperatorEvacOrderFeature) => void;
  /** #459 — start the on-map polygon redraw for the selected evac order (App owns the
   *  draw tool). */
  onCorrectEvacOrderGeometry?: (feature: OperatorEvacOrderFeature) => void;
  /** #459 — auto-enable the operator-evac layer so the operator sees the polygon it is
   *  correcting (reuses #456's post-declare auto-enable). */
  onEnsureOperatorEvacLayerVisible?: () => void;
  /** W.2 editor — called after an identity/population edit so the parent re-fetches
   *  the community map layer (the marker name/label changed). */
  onCommunityEntityChanged?: (id: string) => void;
  /** W.2 editor — called after a soft-retire so the parent refreshes the layer +
   *  clears the selection. */
  onCommunityRetired?: (id: string) => void;
  /** W.2 — fly the camera to a community's linked incident centroid. */
  onFlyToIncident?: (lat: number, lon: number) => void;
  /** §4.4 — open the incident-scoped Evacuation Logistics panel (surfaced from
   *  the community / fire / hotspot detail for flood / ice / multi-hazard). */
  onOpenEvacuation?: (incidentId: string) => void;
  /** §4.4 — fly the camera to a linked community from the evacuation panel
   *  (reuses the incident fly-to controller). */
  onFlyToCommunity?: (lat: number, lon: number) => void;
  /** #458 (role-view council §3) — enter the PEOC "view as <community>" transient
   *  municipal lens over this community (App owns snapshot / apply / restore). */
  onViewAsCommunity?: (community: CommunityFeature) => void;
  /** E.W.0.5 — open the co-located F.1-scored hotspot from a raw hotspot panel. */
  onOpenFusedHotspot?: (id: string) => void;
  /** R.4.2 — called after a staging area is closed so the parent refreshes the
   *  map layer + clears the selection. */
  onStagingAreaChanged?: () => void;
  /** Task #64 — called after a sector metadata/close edit so the parent refreshes
   *  the sectors map layer. */
  onSectorChanged?: () => void;
  /** Task #64 — start redrawing this sector's polygon (App wires the geometry PATCH). */
  onEditSectorGeometry?: (sector: Sector) => void;
  /** #444 blocker #7 — start a fresh sector draw for the selected incident from
   *  FireBody (App's startSectorDraw; promotes the fire to an incident on demand). */
  onDrawSector?: () => void;
  /** SITL surface — open a SITREP from an incident's report chain in the existing
   *  720px document modal (the IncidentManagementSection report chain, §4.19.x). */
  onOpenSitrep?: (id: string) => void;
  /** #185 — open the AssistanceRequestDetail for an assist request linked to the
   *  selected Urgent Alert (the cross-link's Alert → ASSIST direction). */
  onOpenConnectedAssist?: (requestId: string) => void;
  /** #179 — start/stop the operator 3D flythrough around the selected
   *  incident/community site (photoreal tiles stream on demand). */
  onFlythrough?: (target: { lon: number; lat: number; label?: string }) => void;
  /** #179 — whether a flythrough is running (drives the button label/state). */
  flythroughActive?: boolean;
  /** #180 — enter the AAR time replay for a tracked incident (scrub the
   *  recorded audit trail + anomaly lifecycle on the map). */
  onOpenReplay?: (incidentId: string) => void;
  /** #180 — whether the replay is open (disables re-entry). */
  aarReplayActive?: boolean;
  /** #428 — the resolved tracked incidents.id for the current fire/hotspot
   *  selection (App's `selectedIncidentId`), packed into the wall escalation so the
   *  wall can render THAT incident's sectors. null when nothing actionable is
   *  selected / not yet resolved. */
  escalationIncidentId?: string | null;
}

function formatObserved(iso: string): string {
  return new Date(iso)
    .toISOString()
    .replace('T', ' ')
    .replace(/\.\d+Z$/, 'Z');
}

/** UTC HH:MM (matches formatObserved's UTC convention) — the age stamp on the #647 wind fact. */
function formatHm(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  return new Date(t).toISOString().slice(11, 16);
}

/** "2 hours ago" / "3 days ago" relative time, for fire situation reports. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 0) return 'in the future';
  const units: Array<[number, string]> = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.345, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ];
  let value = secs;
  let i = 0;
  while (i < units.length - 1 && value >= units[i]![0]) {
    value = value / units[i]![0];
    i += 1;
  }
  const rounded = Math.floor(value);
  const unit = units[i]![1];
  return `${rounded} ${unit}${rounded === 1 ? '' : 's'} ago`;
}

/** AQI-style colour for a PM2.5 (µg/m³) reading — US EPA breakpoints, coarse.
 *  null for non-PM2.5 observations (no special colour). */
function pm25Color(key: string, value: number): string | null {
  if (key !== 'pm2.5_atm') return null;
  if (value >= 250) return '#7e0023'; // hazardous
  if (value >= 150) return '#8f3f97'; // very unhealthy
  if (value >= 55) return '#ff0000'; // unhealthy
  if (value >= 35) return '#ff7e00'; // unhealthy for sensitive groups
  if (value >= 12) return '#ffff00'; // moderate
  return '#00e400'; // good
}

function SensorBody({ sensor }: { sensor: SensorFeature }): JSX.Element {
  return (
    <div className="px-4 py-4 space-y-4">
      <div>
        <h2 className="text-base font-semibold">{sensor.properties.name}</h2>
        <p className="text-xs text-slate-400 mt-0.5">
          {sensor.properties.source} · {sensor.properties.external_id}
        </p>
        <p className="text-xs text-slate-500 mt-0.5">
          {sensor.geometry.coordinates[1].toFixed(4)}°N, {sensor.geometry.coordinates[0].toFixed(4)}
          °E
        </p>
      </div>
      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
          Latest observations
        </h3>
        {Object.keys(sensor.properties.latest_observations).length === 0 ? (
          <p className="text-sm text-slate-400">No recent observations.</p>
        ) : (
          <table data-testid="obs-table" className="w-full text-sm">
            <tbody>
              {(
                Object.entries(sensor.properties.latest_observations) as Array<
                  [ObservationTypeKey, { value: number; observed_at: string; unit: string }]
                >
              ).map(([key, obs]) => {
                const aqiColor = pm25Color(key, obs.value);
                return (
                <tr key={key} className="border-b border-slate-800 last:border-0">
                  <td className="py-1.5 pr-3 text-slate-300">{OBSERVATION_TYPES[key].label}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">
                    {aqiColor && (
                      <span
                        data-testid="pm25-swatch"
                        className="inline-block w-2 h-2 rounded-full mr-1 align-middle"
                        style={{ backgroundColor: aqiColor }}
                        aria-hidden
                      />
                    )}
                    <span style={aqiColor ? { color: aqiColor } : undefined}>{obs.value.toFixed(1)}</span>
                    <span className="text-slate-400 ml-1">{obs.unit}</span>
                  </td>
                  <td className="py-1.5 text-xs text-slate-500">
                    {formatObserved(obs.observed_at)}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

/**
 * Per-province provincial fire-feed honesty note (#603 item 7 + #640). Reads the
 * shared feed-health heartbeats and surfaces TWO honest states the #337
 * unknown-≠-zero discipline demands, so absence never reads as reassurance:
 *
 *  1. DOWN (heartbeat stale) — the province's fires may be MISSING; reads "feed
 *     unavailable", never "0 fires".
 *  2. #640 REPORTING ZERO (heartbeat LIVE but the feed has produced no fire row) —
 *     the exact SOPFEU failure: it polled `ok` every 15 min yet wrote zero fires
 *     for weeks, so the down signal (heartbeat recency) could never catch it. A
 *     reachable feed showing zero fires is UNKNOWN, not confirmed-zero — stated
 *     plainly. This never blanks the map or suppresses CWFIS (fail LOUD, not shut).
 *
 * A DOWN feed is not also listed as reporting-zero (its own state wins). Renders
 * nothing when every feed is live-and-producing / unknown (never fabricate a
 * fault). Self-contained: degrades to null on any health-fetch error.
 */
export function ProvincialFeedNote(): JSX.Element | null {
  const { provincialFires, nowMs } = useFeedHealth();
  const down = provincialFires.filter((p) => p.state === 'down');
  const reportingZero = provincialFires.filter((p) => p.reportingZero);
  if (down.length === 0 && reportingZero.length === 0) return null;
  const downList = down
    .map(
      (p) =>
        `${p.displayName}${p.lastPolledAtMs ? ` (${humanizeFeedDownAge(nowMs - p.lastPolledAtMs)})` : ''}`,
    )
    .join(', ');
  const zeroList = reportingZero.map((p) => p.displayName).join(', ');
  return (
    <div className="space-y-1">
      {down.length > 0 && (
        <p
          className="text-[10px] text-amber-400/90 leading-relaxed"
          data-testid="provincial-feed-note"
        >
          ⚠ Provincial fire feed unavailable: {downList}. Provincial fires in{' '}
          {down.length > 1 ? 'these provinces' : 'this province'} may be missing — a feed
          outage, not zero fires.
        </p>
      )}
      {reportingZero.length > 0 && (
        <p
          className="text-[10px] text-slate-400/90 leading-relaxed"
          data-testid="provincial-feed-zero-note"
        >
          ⓘ Feed live but reporting 0 fires: {zeroList}. Zero fires is not independently confirmed —
          a live feed with no rows can mean a quiet season or a feed problem.
        </p>
      )}
    </div>
  );
}

function fireDisplayName(p: ActiveFireFeature['properties']): string {
  if (p.fire_id) return p.agency ? `${p.agency} ${p.fire_id}` : p.fire_id;
  return p.agency ? `${p.agency} wildfire` : 'Wildland fire';
}

/** Migration 0154 — the FACT-ONLY live context for the comms-thread hazard header on a fire panel:
 *  area · distance · access. The SAME facts already rendered on the urgent OC-fire alert, reused so
 *  the coordination header names what the operator is looking at. No adjective, no conclusion. */
function fireHazardContext(p: ActiveFireFeature['properties']): string | undefined {
  const tc = p.threatened_community;
  const parts: string[] = [];
  if (p.hectares_estimated != null)
    parts.push(`${p.hectares_estimated.toLocaleString(undefined, { maximumFractionDigits: 0 })} ha`);
  if (tc?.distance_m != null) parts.push(`${Math.round(tc.distance_m / 1000)} km`);
  const access = tc ? accessTypeLabel(tc.access_type) : null;
  if (access) parts.push(access);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function FireStageBadge({ stage }: { stage: string | null }): JSX.Element {
  const style = fireStageStyle(stage);
  return (
    <span
      data-testid="fire-stage-badge"
      className="inline-block text-[10px] px-1.5 py-0.5 rounded font-medium"
      style={{ backgroundColor: `${style.color}33`, color: style.color }}
    >
      {style.label}
    </span>
  );
}

/** Task #93 — the unified resource badge: shows the IMS Resource Status Lifecycle
 *  `lifecycle_state` (the R.4.1/R.4.2 source of truth) rather than the legacy
 *  `status` flag, coloured by the context-locked lifecycleStates palette. */
function LifecycleStateBadge({ state }: { state: string | null }): JSX.Element {
  const style = lifecycleStateStyle(state);
  return (
    <span
      data-testid="lifecycle-state-badge"
      className="text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0"
      style={{ backgroundColor: `${style.color}33`, color: style.color }}
      title={`Resource lifecycle state: ${style.label}`}
    >
      {style.label}
    </span>
  );
}

/** §4.4 — the call-to-action that opens the incident-scoped Evacuation Logistics
 *  panel from a fire / hotspot detail (rendered only on flood/ice/multi incidents). */
function EvacuationEntryButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      data-testid="open-evacuation"
      onClick={onClick}
      className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded bg-rose-600/90 hover:bg-rose-600 border border-rose-500 text-white text-sm font-medium"
    >
      <span aria-hidden>🚁</span> Evacuation Logistics
    </button>
  );
}

/**
 * #635 — the KEPT fail-loud honesty caption, repurposed from the DELETED "Escalation risk"
 * section's `fire-escalation-degraded` chip. Four of five §G seats ruled the escalation
 * index + band OFF the operator surface (docs/fire-scorer-rebuild-g-gate-2026-07-13.md): a
 * number that can go DOWN is a de-escalation channel on a live hazard, and the recommend
 * block did the pilot's and charter's job on a pre-incident hazard with no IC. The scorer
 * stays INTERNAL (queue-ordering input only); its numeral, band, narrative, "wind-toward-
 * values" copy, and the drone-tasking RECOMMENDATION are gone from every operator surface.
 *
 * What survives is the ONE genuine unknown the score still detects: when SCORING STOPPED, the
 * fire's standing is UNTRUSTED, so the machine Threat Level may be understated (the RL ladder
 * has stopped honouring the stale ≥60 escalator). A single line, in the score's own voice,
 * shown ONLY in that stale state and SUPPRESSED entirely when the signal is fresh — its RARITY
 * is the signal (gate §9). It renders NO numeral, NO band, NO adjective, NO recommendation.
 */
/**
 * #647 Wave 3 — the fact-only DOWNWIND line for the OC-fire panel. Returns the sentence ONLY
 * when the wind is KNOWN (downwind true/false, fresh wind + a bearing); returns null when the
 * wind is UNKNOWN (downwind === null) — the honesty caption states the unknown instead, so this
 * line never renders an in-path / not-in-path claim on an unknown wind (#647 PIN 3).
 *
 * States the OBSERVED wind, the community's bearing FROM THE FIRE (the join's community→fire
 * bearing INVERTED to fire→community), whether it is inside the ±45° path, the access fact, and
 * the reading's age. ⛔ NO arrow, NO adjective, NO conclusion, NO "toward" (that asserts
 * transport + arrival — a prediction), NO recommendation, NO all-clear (#647 PIN 6).
 */
export function buildDownwindFactLine(
  tc: NonNullable<ActiveFireFeature['properties']['threatened_community']>,
): string | null {
  if (tc.downwind == null || !tc.wind || tc.bearing_deg == null) return null;
  const deg3 = (n: number): string => String(((Math.round(n) % 360) + 360) % 360).padStart(3, '0');
  const speed = Math.round(tc.wind.speed_kmh);
  const distKm = tc.distance_m != null ? Math.round(tc.distance_m / 1000) : null;
  const path = tc.downwind ? 'in wind path (±45°)' : 'not in wind path';
  const access = accessTypeLabel(tc.access_type);
  const observed = formatHm(tc.wind.observed_at);
  const who = tc.name ?? 'The community';
  // bearing FROM the fire = the join's community→fire bearing inverted (+180).
  const bearingPart =
    distKm != null
      ? `${who} bears ${deg3(tc.bearing_deg + 180)}°, ${distKm} km — ${path}.`
      : `${who} bears ${deg3(tc.bearing_deg + 180)}° — ${path}.`;
  return [
    `Wind ${deg3(tc.wind.direction_deg)}° @ ${speed} km/h.`,
    bearingPart,
    access ? `${access}.` : null,
    observed ? `Observed ${observed}.` : null,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * #667 — the community-focused in-wind-path line for a SECONDARY threatened community (an
 * in-radius community BEYOND the nearest whose three-state wind-path FACT is TRUE). Shorter than
 * buildDownwindFactLine — the fire's observed wind + reading age are already stated on the nearest,
 * so this line names the community, its access FACT, its distance + compass (community→fire), and
 * the neutral "in wind path (±45°)" phrasing VERBATIM the nearest's (#647). Returns null unless
 * downwind === true (a KNOWN in-path FACT); a null (UNKNOWN) or false community is NEVER rendered
 * here (null surfaces through the honesty caption; false is a known not-in-path fact that adds
 * nothing). ⛔ NO arrow, adjective, "toward", recommendation, all-clear, or imperative.
 */
export function buildInPathCommunityLine(
  c: NonNullable<ActiveFireFeature['properties']['threatened_community']>,
): string | null {
  if (c.downwind !== true) return null;
  const who = c.name ?? 'A community';
  const access = accessTypeLabel(c.access_type);
  const distKm = c.distance_m != null ? Math.round(c.distance_m / 1000) : null;
  const parts = [
    access,
    distKm != null ? `${distKm} km${c.compass ? ` ${c.compass}` : ''}` : null,
    'in wind path (±45°)',
  ].filter(Boolean);
  return `${who} — ${parts.join(' · ')}`;
}

export function FireThreatSignalCaption({ p }: { p: ActiveFireFeature['properties'] }): JSX.Element | null {
  const escalationStale =
    p.escalation_index != null && !isEscalationFresh(p.escalation_updated_at, Date.now());
  // #647 — the caption also fails LOUD when this fire THREATENS a community but the wind is
  // UNKNOWN (missing / stale / calm), so the COP cannot state whether the community is in the
  // fire's wind path. `downwind === null` is that known-unknown (STRICT null — an older payload
  // that omits the field is `undefined` and does NOT trip the caption). Rare by construction
  // (only an urgent OC fire near a community with no fresh wind); suppressed entirely otherwise,
  // so a wall of grey UNKNOWNs never becomes its own reassurance failure (gate §9).
  const windUnknown = p.threatened_community != null && p.threatened_community.downwind === null;
  if (!escalationStale && !windUnknown) return null;
  return (
    <div data-testid="fire-threat-signal-degraded" className="space-y-0.5">
      {escalationStale && (
        <p
          className="text-[11px] font-medium text-amber-400"
          title={p.escalation_updated_at ? formatObserved(p.escalation_updated_at) : undefined}
        >
          ⚠ Threat Level may be understated — monitoring signal degraded
          {p.escalation_updated_at ? `, last observed ${relativeTime(p.escalation_updated_at)}` : ''}.
        </p>
      )}
      {windUnknown && (
        <p className="text-[11px] font-medium text-amber-400" data-testid="oc-fire-wind-unknown">
          Wind unknown — direction not established.
        </p>
      )}
    </div>
  );
}

function FireBody({
  fire,
  onSelectCamera,
  onSelectAsset,
  onSelectAircraft,
  onOpenEvacuation,
  onOpenSitrep,
  onFlythrough,
  flythroughActive = false,
  onOpenReplay,
  aarReplayActive = false,
  onFlyToIncident,
  onSelectMission,
  onDrawSector,
}: {
  fire: ActiveFireFeature;
  onSelectCamera?: (c: CameraFeature) => void;
  onSelectAsset?: (a: ResponseAssetFeature) => void;
  onSelectAircraft?: (a: AircraftFeature) => void;
  onOpenEvacuation?: (incidentId: string) => void;
  onOpenSitrep?: (id: string) => void;
  /** #444 blocker #7 (Field IC) — start the IMS §5.3 sector draw for THIS fire's
   *  incident from the panel the alert opens (not just the map toolbar). Promotes the
   *  fire to an incident on demand via App's ensureIncidentId. */
  onDrawSector?: () => void;
  /** #179 — start/stop the operator 3D flythrough around this fire's site. */
  onFlythrough?: (target: { lon: number; lat: number; label?: string }) => void;
  flythroughActive?: boolean;
  /** #180 — enter the AAR time replay for this fire's tracked incident. */
  onOpenReplay?: (incidentId: string) => void;
  aarReplayActive?: boolean;
  /** #356 — re-centre the COP on this fire. */
  onFlyToIncident?: (lat: number, lon: number) => void;
  /** Phase 4e — feed click-through on the incident's drone list (§I.2). */
  onSelectMission?: (m: DroneMissionView) => void;
}): JSX.Element {
  const p = fire.properties;
  const [lon, lat] = p.centroid;
  // #667 — the SET beyond the nearest: every OTHER in-radius community (the singular is the
  // nearest, kept byte-identical). Partitioned by the HONEST three-state wind-path FACT
  // (threatened_communities[].downwind — NEVER the flattened hotspot boolean): TRUE renders an
  // "in wind path" line; NULL (UNKNOWN wind) surfaces through ONE caption, never dropped as safe,
  // never "not in wind path"; FALSE (a KNOWN not-in-path fact) adds nothing beyond the nearest.
  const ocOtherCommunities = (p.threatened_communities ?? []).filter(
    (c) => c.id !== p.threatened_community?.id,
  );
  const ocOthersInWindPath = ocOtherCommunities.filter((c) => c.downwind === true);
  const ocOthersWindUnknown = ocOtherCommunities.filter((c) => c.downwind === null);
  // Task #92 — the tracked incident this fire was promoted to (if any). On open
  // we look it up (idempotent — the fire may have been promoted in a prior
  // session); "Recommend Dispatch" sets it from the recommendation response.
  const [incidentId, setIncidentId] = useState<string | null>(null);

  // IMS audit: log when the operator opens a fire's detail panel.
  useEffect(() => {
    logAction('view_fire_detail', {
      fire_id: p.id,
      source: p.source,
      stage_of_control: p.stage_of_control_status,
      response_level: p.response_level,
    });
  }, [p.id, p.source, p.stage_of_control_status, p.response_level]);

  // Resolve any incident this fire was already promoted to, so the work-surface's
  // management section + resource thread show on re-open (not only right after
  // dispatching). The linked-community count (Evacuation gate) is fetched by the
  // work-surface itself from the incident detail.
  useEffect(() => {
    const ctl = new AbortController();
    setIncidentId(null);
    void fetchIncidentForSource('active_wildland_fire', p.id, ctl.signal)
      .then((inc) => setIncidentId(inc?.id ?? null))
      .catch(() => undefined);
    return () => ctl.abort();
  }, [p.id]);
  return (
    <div className="px-4 py-4 space-y-4" data-testid="fire-body">
      <div>
        {/* IMS terminology: this entity is a "Reported Active Wildland Fire". */}
        <p className="text-[10px] uppercase tracking-wider text-slate-500">
          Reported Active Wildland Fire
        </p>
        <h2 className="text-base font-semibold">{fireDisplayName(p)}</h2>
        <p className="text-xs text-slate-400 mt-0.5">
          {fireSourceLabel(p.source)}
          {p.agency ? ` · ${p.agency}` : ''}
        </p>
        <p className="text-xs text-slate-500 mt-0.5">
          {lat.toFixed(4)}°N, {lon.toFixed(4)}°E
        </p>
        {p.response_level != null && (
          <div className="mt-2">
            {/* #613 — a reported wildland fire's response_level is the nearest fused
                hotspot's machine-derived level (raw, un-owned) → the Threat axis. */}
            <ResponseLevelBadge level={p.response_level} axis="threat" />
          </div>
        )}
        {/* #356 — jump the COP to this fire (it doesn't auto-fly on select). */}
        <div className="mt-3">
          <FlyToIncidentButton lat={lat} lon={lon} onFlyToIncident={onFlyToIncident} />
        </div>
        {/* Phase 5b — the owned-incident read on a fire (the fire↔incident thread),
            mirroring HighRiskHotspotBody. Driven by the API join on the feature
            (active_wildland_fires LEFT JOIN incidents ON source_record_id); the MAP
            carries the uniform managed ring, this badge carries the incident number.
            Panel-only — NO incident-number map label. */}
        {p.owned_incident && (
          <div className="mt-2">
            <span
              data-testid="fire-owned-incident"
              className="inline-flex items-center gap-1 rounded bg-indigo-900/40 px-1.5 py-0.5 text-[10px] font-medium text-indigo-300"
              title={`Owned — tracked as incident ${p.owned_incident.incident_number} (${p.owned_incident.status})`}
            >
              <span aria-hidden>🔗</span> Owned · incident {p.owned_incident.incident_number}
            </span>
          </div>
        )}
        {/* Ownership-coordination — the cross-agency "who holds command + how to reach them" read,
            visible to every agency (from the incident_common_picture view, not the RLS-gated fetch). */}
        {p.owned_incident && <OwnershipCoordinationBlock owned={p.owned_incident} />}
      </div>
      {/* #444 source 2 — the OC-fire URGENT alert (risk-gated; only RL≥3 shows here).
          Names the threatened community so the municipal/PEOC seat can act, distinct
          from the hotspot-corroboration response_level above. */}
      {p.oc_fire_response_level != null && p.oc_fire_response_level >= 3 && (
        <section
          data-testid="oc-fire-alert"
          className="rounded border border-red-900/50 bg-red-950/30 px-3 py-2"
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-red-300">
              Urgent · OC wildfire
            </span>
            {/* #613 — the OC-fire risk-gated ladder is a machine-derived score → Threat axis. */}
            <ResponseLevelBadge level={p.oc_fire_response_level} axis="threat" />
          </div>
          {p.threatened_community?.name ? (
            <p className="mt-1 text-sm text-red-100" data-testid="oc-fire-threatened-community">
              Threatens <span className="font-semibold">{p.threatened_community.name}</span>
              {/* #632/#635 — the ACCESS FACT, fact-only ("Access: fly-in", never "no road out";
                  founder ruling 2026-07-13 — the egress conclusion belongs to the pilot). A
                  fly-in community is categorically more exposed; render the fact alongside the
                  name. Road communities add nothing (accessTypeLabel → null); 'unknown' reads
                  "access unconfirmed" (never reassuring). */}
              {accessTypeLabel(p.threatened_community.access_type) && (
                <span className="font-semibold text-amber-200" data-testid="oc-fire-access-fact">
                  {' — '}
                  {accessTypeLabel(p.threatened_community.access_type)}
                </span>
              )}
              {p.threatened_community.distance_m != null && (
                <>
                  {' · '}
                  {Math.round(p.threatened_community.distance_m / 1000)} km{' '}
                  {p.threatened_community.compass ?? ''}
                </>
              )}
              {p.threatened_community.population != null && (
                <span className="text-red-300/70">
                  {' · pop. '}
                  {p.threatened_community.population.toLocaleString()}
                </span>
              )}
              {/* #635 — the primary aerodrome IDENT completes the fact set the COP already
                  computes (name · pop · distance · access · aerodrome). Identifier only — a
                  FACT, no observed condition, no conclusion (that would be the pilot's job). */}
              {p.threatened_community.primary_aerodrome_ident && (
                <span className="text-red-300/70" data-testid="oc-fire-aerodrome">
                  {' · '}
                  {p.threatened_community.primary_aerodrome_ident}
                </span>
              )}
            </p>
          ) : (
            <p className="mt-1 text-xs text-red-200/80">
              {(p.hectares_estimated ?? 0) >= 1000
                ? 'Large out-of-control fire.'
                : 'Out-of-control fire on the urgent surface.'}
            </p>
          )}
          {/* #647 Wave 3 — the DOWNWIND FACT, beneath the community facts. Rendered ONLY when the
              wind is KNOWN (fact-only, one neutral line: observed wind · community bearing from the
              fire · in/out of the ±45° path · access · age). The UNKNOWN state renders through the
              honesty caption below ("Wind unknown — direction not established."), never here — so no
              in-path claim is ever made off an unknown wind. NEVER a score, arrow, or conclusion. */}
          {p.threatened_community && buildDownwindFactLine(p.threatened_community) && (
            <p className="mt-1 text-xs text-red-200/90" data-testid="oc-fire-wind-fact">
              {buildDownwindFactLine(p.threatened_community)}
            </p>
          )}
          {/* #667 — the SET: every OTHER in-radius community (beyond the nearest) IN the fire's
              wind path (three-state FACT === true), each named with the neutral #647 "in wind path"
              fact. THE FIX for the singular-payload bug: a fire in the wind path of a 2nd community
              used to raise NOTHING for it. A community with UNKNOWN wind (null) surfaces through the
              caption below, NEVER dropped as safe, NEVER "not in wind path". NO score, arrow,
              conclusion, or imperative. */}
          {ocOthersInWindPath.length > 0 && (
            <div className="mt-1 space-y-0.5" data-testid="oc-fire-additional-communities">
              {ocOthersInWindPath.map((c) => (
                <p
                  key={c.id}
                  className="text-xs text-red-200/90"
                  data-testid="oc-fire-additional-community"
                >
                  {buildInPathCommunityLine(c)}
                </p>
              ))}
            </div>
          )}
          {/* #667 / class-defect R3+R4 — when OTHER in-radius communities have UNKNOWN wind, surface
              them in ONE caption (never a wall): the fire threatens them too, but the COP cannot
              state a wind path. NEVER "clear" / "not threatened" / dropped as safe. */}
          {ocOthersWindUnknown.length > 0 && (
            <p
              className="mt-1 text-[11px] font-medium text-amber-400"
              data-testid="oc-fire-additional-unknown"
            >
              Wind unknown — direction not established for {ocOthersWindUnknown.length} other{' '}
              communit{ocOthersWindUnknown.length === 1 ? 'y' : 'ies'} in range:{' '}
              {ocOthersWindUnknown.map((c) => c.name ?? 'unnamed').join(', ')}.
            </p>
          )}
          {p.severe_cap_proximate && (
            <p className="mt-1 text-xs text-amber-300" data-testid="oc-fire-severe-cap">
              ⚠ Under a severe weather alert{p.severe_cap_event ? `: ${p.severe_cap_event}` : ''}
            </p>
          )}
        </section>
      )}
      {/* #635 — the escalation index/band/narrative/recommend surface is DELETED (scorer is now
          INTERNAL, queue-ordering only). The KEPT fail-loud honesty caption survives here; the
          threatened-community FACTS render on the urgent OC-fire alert section above. */}
      <FireThreatSignalCaption p={p} />
      {/* §G rebuild Wave 2 (migration 0154) — the DOCTRINAL SPINE. Ontario IMS §4.2: during
          wildfire evacuations the PEOC "maintains a regular communication cycle with host and
          evacuated communities." When this fire threatens a community, mount the shipped #420a
          coordination thread bound to it, so the PEOC's relay leaves a timestamped, read-receipted,
          HAZARD-LINKED record (the First Nation seat's ruling — provenance a phone call can't
          leave). Every message posted here is stamped to THIS fire. No thread mounts on a fire with
          no community to talk to. NO pre-filled body, NO recommendation, NO score, NO all-clear. */}
      {p.threatened_community?.id && (
        <section data-testid="fire-community-comms">
          <CommunityCommsThread
            communityId={p.threatened_community.id}
            boundLabel={p.threatened_community.name ?? undefined}
            hazard={{
              sourceType: 'active_wildland_fire',
              sourceId: p.id,
              ref: p.fire_id,
              contextLabel: fireHazardContext(p),
            }}
          />
        </section>
      )}
      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">Details</h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">Stage of control</dt>
          <dd>
            <FireStageBadge stage={p.stage_of_control_status} />
          </dd>
          <dt className="text-slate-400">Response</dt>
          <dd className="text-sm" data-testid="fire-response-type">
            {responseTypeLabel(p.response_type)}
            {/* #618 Fix 1b — attributed provenance: what the PROVINCE actually filed, so a fire
                reading "Not reported by source" over a CWFIS-normalized class is legible. The raw
                response code (MNP/-1) stays out of the operator's flow (honesty lock) — only in the
                hover title; the stage gets a plain-language attributed gloss. */}
            {upstreamStageGloss(p.upstream_stage_raw) && (
              <span
                className="block text-[10px] text-slate-500"
                data-testid="fire-upstream-provenance"
                title={`As filed by ${p.agency ?? 'the source'}${
                  p.upstream_response_type_raw
                    ? ` — response code “${p.upstream_response_type_raw}” (not a CIFFC class)`
                    : ''
                }`}
              >
                {p.agency ?? 'Source'} reports “{upstreamStageGloss(p.upstream_stage_raw)}”
              </span>
            )}
          </dd>
          <dt className="text-slate-400">Size</dt>
          <dd className="font-mono">
            {p.hectares_estimated != null
              ? `${p.hectares_estimated.toLocaleString(undefined, { maximumFractionDigits: 1 })} ha`
              : '—'}
          </dd>
          {p.has_perimeter && p.perimeter_area_km2 != null && (
            <>
              <dt className="text-slate-400">Perimeter area</dt>
              <dd className="font-mono">
                {p.perimeter_area_km2.toLocaleString(undefined, { maximumFractionDigits: 1 })} km²
                <span
                  className="ml-1 text-[10px] text-slate-500"
                  title="Satellite-derived M3 hotspot perimeter — an estimate, not a surveyed boundary"
                >
                  est.
                </span>
              </dd>
            </>
          )}
          <dt className="text-slate-400">Observed</dt>
          <dd className="text-xs" title={formatObserved(p.observed_at)}>
            {relativeTime(p.observed_at)}
          </dd>
          {p.last_updated_at && (
            <>
              <dt className="text-slate-400">Last updated</dt>
              <dd className="text-xs" title={formatObserved(p.last_updated_at)}>
                {relativeTime(p.last_updated_at)}
              </dd>
            </>
          )}
        </dl>
      </section>

      {/* #179 — operator 3D flythrough around the fire site (on-demand
          photoreal tiles; prior view restored when the tour ends). */}
      {onFlythrough && (
        <FlythroughButton
          target={{ lon, lat, label: fireDisplayName(p) }}
          active={flythroughActive}
          onFlythrough={onFlythrough}
        />
      )}

      {/* Coordination re-arch Phase 2 — the SAME work-surface HighRiskHotspotBody +
          the Incidents-tab incident detail render (IMS Ruling 1). A fire-sourced
          incident is now worked from one place with the identical action set +
          resource thread (declare → confirm → recommend dispatch → request a drone →
          stage resources → evacuate → close), no longer a hand-rolled twin. The two
          fire-only affordances (IncidentDronesPanel #345, the Evacuation entry) live
          in the surface, so the Incidents-tab view is not feature-poorer. */}
      <IncidentWorkSurface
        source={{
          recordType: 'active_wildland_fire',
          recordId: p.id,
          lat,
          lon,
          aoLabel: 'this reported fire',
          declareName: fireDisplayName(p),
          declareHazardType: 'wildfire',
        }}
        incidentId={incidentId}
        lat={lat}
        lon={lon}
        onIncidentDeclared={(inc) => setIncidentId(inc.id)}
        onIncidentPromoted={(id) => setIncidentId(id)}
        onSelectCamera={onSelectCamera}
        onSelectAsset={onSelectAsset}
        onSelectAircraft={onSelectAircraft}
        onOpenSitrep={onOpenSitrep}
        onOpenEvacuation={onOpenEvacuation}
        onOpenReplay={onOpenReplay}
        onSelectMission={onSelectMission}
        replayActive={aarReplayActive}
      />

      {/* #444 blocker #7 (Field IC) — divide the incident into IMS §5.3 sectors from
          here, not only the map toolbar. Enabled once the fire is an owned incident
          (Declare above first); reuses App's sector draw + auto-promote. */}
      {onDrawSector && incidentId && (
        <section>
          <button
            type="button"
            data-testid="fire-draw-sector"
            onClick={onDrawSector}
            className="w-full rounded border border-slate-700 bg-slate-800/60 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-700/60"
            title="Divide the selected incident into geographic sectors (IMS §5.3)"
          >
            Divide into sectors
          </button>
        </section>
      )}

      <p className="text-[10px] text-slate-500 leading-relaxed">
        Reported by {fireSourcesLabel(p.sources, p.source)}. Same-fire reports across
        the national (CWFIS) and provincial feeds are de-duplicated by fire number at
        query time, keeping the freshest stage of control.
      </p>
      <ProvincialFeedNote />
    </div>
  );
}

/** Great-circle distance in km (haversine). */
function haversineKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371.0088;
  const toRad = (d: number): number => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial compass bearing (0–360°) from one point toward another. */
function bearingDeg(fromLat: number, fromLon: number, toLat: number, toLon: number): number {
  const toRad = (d: number): number => (d * Math.PI) / 180;
  const dLon = toRad(toLon - fromLon);
  const y = Math.sin(dLon) * Math.cos(toRad(toLat));
  const x =
    Math.cos(toRad(fromLat)) * Math.sin(toRad(toLat)) -
    Math.sin(toRad(fromLat)) * Math.cos(toRad(toLat)) * Math.cos(dLon);
  return (((Math.atan2(y, x) * 180) / Math.PI) + 360) % 360;
}

/** Smallest absolute angle (0–180°) between two compass bearings. */
function angularDiff(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

const NEARBY_CAMERA_RADIUS_KM = 10;
const LINE_OF_SIGHT_TOLERANCE_DEG = 30;
const KM_PER_DEG_LAT = 111.32;

interface NearbyCamera {
  feature: CameraFeature;
  distanceKm: number;
}

/**
 * Cameras within 10 km of the hotspot that might SEE its smoke: smoke-priority,
 * live, and (when their heading is known) aimed within ±30° of the bearing to
 * the hotspot. Heading-unknown cams are kept (omnidirectional coverage). Nearest
 * five. Used for the operator's visual-confirmation step (detect → confirm).
 */
function useNearbyCameras(
  lon: number,
  lat: number,
  opts: { smokePriority?: boolean; requireLineOfSight?: boolean; radiusKm?: number } = {},
): NearbyCamera[] {
  const { smokePriority = true, requireLineOfSight = true, radiusKm = NEARBY_CAMERA_RADIUS_KM } = opts;
  const [cameras, setCameras] = useState<NearbyCamera[]>([]);
  useEffect(() => {
    const ctl = new AbortController();
    const dLat = radiusKm / KM_PER_DEG_LAT;
    const cosLat = Math.max(0.05, Math.cos((lat * Math.PI) / 180));
    const dLon = radiusKm / (KM_PER_DEG_LAT * cosLat);
    const bbox = { west: lon - dLon, east: lon + dLon, south: lat - dLat, north: lat + dLat };
    void fetchCameras(bbox, ctl.signal, { smokePriority })
      .then((fc) => {
        const out: NearbyCamera[] = [];
        for (const f of fc.features) {
          if (f.properties.status !== 'live') continue;
          const [clon, clat] = f.geometry.coordinates;
          const distanceKm = haversineKm(clat, clon, lat, lon);
          if (distanceKm > radiusKm) continue;
          // Line-of-sight ±30° (cameras AIMED at the point) — meaningful for a
          // pinpoint smoke source, but not for a broad weather-warning area, so the
          // CAP panel opts out and just lists the nearest live cameras.
          if (requireLineOfSight && f.properties.heading_known) {
            const bearing = bearingDeg(clat, clon, lat, lon);
            if (angularDiff(bearing, f.properties.heading_deg) > LINE_OF_SIGHT_TOLERANCE_DEG) continue;
          }
          out.push({ feature: f, distanceKm });
        }
        out.sort((a, b) => a.distanceKm - b.distanceKm);
        setCameras(out.slice(0, 5));
      })
      .catch(() => undefined);
    return () => ctl.abort();
  }, [lon, lat, smokePriority, requireLineOfSight, radiusKm]);
  return cameras;
}

/**
 * Reusable "Nearby cameras" list (#357). Renders the nearest live cameras around an
 * incident point as clickable rows (snapshot thumb + name + distance).
 *
 * Tuning is hazard-aware (#443) via `cameraTuning`:
 *  - 'fire' (default) — smoke-priority + line-of-sight: cameras that could SEE the
 *    smoke of a pinpoint wildfire/hotspot. Byte-identical to the original behaviour.
 *  - 'all-nearby' — every live camera in range, distance-ranked (NO smoke-priority,
 *    NO line-of-sight). Correct for a flood / ice / CAP weather AREA, where road,
 *    river-gauge and community cams matter, not just the smoke-visible subset.
 * 'all-nearby' overrides the granular smokePriority/requireLineOfSight props.
 */
function NearbyCamerasSection({
  lon,
  lat,
  onSelectCamera,
  cameraTuning = 'fire',
  smokePriority = true,
  requireLineOfSight = true,
  radiusKm = NEARBY_CAMERA_RADIUS_KM,
  emptyLabel,
}: {
  lon: number;
  lat: number;
  onSelectCamera?: (c: CameraFeature) => void;
  cameraTuning?: 'fire' | 'all-nearby';
  smokePriority?: boolean;
  requireLineOfSight?: boolean;
  radiusKm?: number;
  emptyLabel?: string;
}): JSX.Element {
  // 'all-nearby' drops the smoke-priority + line-of-sight filters so road / river-gauge
  // / community cams around a flood / ice / CAP area aren't excluded with the smoke set.
  const allNearby = cameraTuning === 'all-nearby';
  const nearby = useNearbyCameras(lon, lat, {
    smokePriority: allNearby ? false : smokePriority,
    requireLineOfSight: allNearby ? false : requireLineOfSight,
    radiusKm,
  });
  const effEmptyLabel =
    emptyLabel ??
    (allNearby
      ? 'No live cameras within range.'
      : 'No smoke-priority live cameras with line of sight.');
  return (
    <section>
      <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
        Nearby cameras
        {nearby.length > 0 && <span className="text-slate-500"> · {nearby.length}</span>}
      </h3>
      {nearby.length === 0 ? (
        <p className="text-sm text-slate-500">{effEmptyLabel}</p>
      ) : (
        <ul className="space-y-1.5" data-testid="nearby-cameras">
          {nearby.map(({ feature, distanceKm }) => (
            <li key={feature.id}>
              <button
                type="button"
                onClick={() => onSelectCamera?.(feature)}
                className="w-full flex items-center gap-2 text-left p-1.5 rounded bg-slate-800/60 hover:bg-slate-700 border border-slate-700"
              >
                <img
                  src={`/api/cameras/${feature.id}/snapshot`}
                  alt=""
                  className="w-12 h-9 object-cover rounded bg-slate-900 flex-shrink-0"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                  }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs truncate">{feature.properties.name}</span>
                  <span className="block text-[10px] text-slate-400">
                    {distanceKm.toFixed(1)} km · {feature.properties.source}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Incident-anchored "Request live eyes" (#348b) — the drone marketplace front door,
 * mounted on every actionable incident panel (fire / hotspot / CAP / anomaly). The
 * area of interest IS the selected marker, so the match flow opens reachability-gated
 * and ready (no separate incident selection needed). Thin wrapper that fixes the AO
 * to the marker point; reuses the self-contained RequestLiveEyes component.
 */
function IncidentRequestLiveEyes({
  lat,
  lon,
  aoLabel,
  incidentId = null,
}: {
  lat: number;
  lon: number;
  aoLabel: string;
  incidentId?: string | null;
}): JSX.Element {
  return <RequestLiveEyes ao={{ lat, lon }} aoLabel={aoLabel} incidentId={incidentId} />;
}

/** One ✓/✗ corroborating-signal row. */
function SignalRow({ label, on, detail }: { label: string; on: boolean; detail?: string }): JSX.Element {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className={on ? 'text-emerald-400' : 'text-slate-600'} aria-hidden>
        {on ? '✓' : '✗'}
      </span>
      <span className={on ? 'text-slate-200' : 'text-slate-500'}>{label}</span>
      {detail && <span className="text-xs text-slate-400 ml-auto">{detail}</span>}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * R.3 click-fire UX — Nearby Response Assets, Live Aircraft, dispatch.
 * Shared by FireBody (active fire) and HighRiskHotspotBody (fused hotspot).
 * ------------------------------------------------------------------ */

/** Emoji glyph per asset class — a quick visual type cue in the list. */
const ASSET_TYPE_ICON: Record<string, string> = {
  fire_station: '🚒',
  wildfire_base: '✈️',
  aviation_base: '🚁',
  aviation_aircraft: '🛩️',
  marine_vessel: '🚤',
  medical_facility: '🏥',
  volunteer_chapter: '🤝',
  utility_response: '⚡',
  specialty_team: '🎯',
};

function assetTypeLabel(type: string | null): string {
  if (!type) return 'resource';
  return type.replace(/_/g, ' ');
}

/**
 * Nearby Response Assets (R.3.2) — fire stations, aircraft/water-bomber bases,
 * SAR units within 50 km, nearest first. Each row is clickable → opens that
 * asset's detail panel. Logs `view_nearby_assets` once when the section first
 * has data for an incident.
 */
function NearbyResponseAssets({
  lat,
  lon,
  incidentType,
  incidentId,
  onSelectAsset,
}: {
  lat: number;
  lon: number;
  // Local widening for the #357 CAP/tornado reuse — this value is only an audit-log
  // payload (`view_nearby_assets`), NOT a write against the dispatch IncidentType
  // schema (which stays ['high_risk_hotspot','active_fire']).
  incidentType: IncidentType | 'cap_alert';
  incidentId: string;
  onSelectAsset?: (a: ResponseAssetFeature) => void;
}): JSX.Element {
  const [assets, setAssets] = useState<NearbyAsset[] | null>(null);

  useEffect(() => {
    const ctl = new AbortController();
    setAssets(null);
    void fetchNearbyAssets(lat, lon, ctl.signal, { radiusKm: 50, limit: 10 })
      .then((rows) => {
        setAssets(rows);
        logAction('view_nearby_assets', {
          incident_type: incidentType,
          incident_id: incidentId,
          count: rows.length,
        });
      })
      .catch(() => setAssets([]));
    return () => ctl.abort();
  }, [lat, lon, incidentType, incidentId]);

  const openAsset = (id: string): void => {
    void fetchResponseAsset(id)
      .then((feat) => {
        // The detail payload is a superset of a list feature (geometry nullable);
        // it slots straight into the side panel, same as the App deep-link.
        if (feat && feat.geometry) onSelectAsset?.(feat as unknown as ResponseAssetFeature);
      })
      .catch(() => undefined);
  };

  return (
    <section>
      <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
        Nearby Response Resources
        {assets && assets.length > 0 && <span className="text-slate-500"> · {assets.length}</span>}
      </h3>
      {assets === null ? (
        <p className="text-sm text-slate-500">Finding nearest resources…</p>
      ) : assets.length === 0 ? (
        <p className="text-sm text-slate-500">No response resources within 50 km.</p>
      ) : (
        <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="nearby-assets">
          {assets.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => openAsset(a.id)}
                className="w-full flex items-center gap-2 text-left p-1.5 rounded bg-slate-800/60 hover:bg-slate-700 border border-slate-700"
              >
                <span className="text-base flex-shrink-0" aria-hidden>
                  {ASSET_TYPE_ICON[a.type ?? ''] ?? '📍'}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs truncate">{a.name}</span>
                  <span className="block text-[10px] text-slate-400">
                    {assetTypeLabel(a.type)} · {a.distance_km.toFixed(1)} km {a.bearing}
                  </span>
                </span>
                <LifecycleStateBadge state={a.lifecycle_state} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Live Aircraft (R.3.3) — recent ADS-B tracks within 200 km, nearest first.
 * Water bombers are highlighted orange. Each row → that aircraft's detail panel.
 */
function LiveAircraftSection({
  lat,
  lon,
  onSelectAircraft,
}: {
  lat: number;
  lon: number;
  onSelectAircraft?: (a: AircraftFeature) => void;
}): JSX.Element | null {
  const [aircraft, setAircraft] = useState<NearbyAircraft[] | null>(null);

  useEffect(() => {
    const ctl = new AbortController();
    setAircraft(null);
    void fetchNearbyAircraft(lat, lon, ctl.signal, { radiusKm: 200, limit: 5 })
      .then(setAircraft)
      .catch(() => setAircraft([]));
    return () => ctl.abort();
  }, [lat, lon]);

  const openAircraft = (id: string): void => {
    void fetchAircraftDetail(id)
      .then((feat) => {
        if (feat && feat.geometry) onSelectAircraft?.(feat as unknown as AircraftFeature);
      })
      .catch(() => undefined);
  };

  // Hide the section entirely when there are no live tracks nearby — common, and
  // an empty box adds noise. (Still rendered while loading, to avoid a flash.)
  if (aircraft !== null && aircraft.length === 0) return null;

  return (
    <section>
      <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
        Live Aircraft
        {aircraft && aircraft.length > 0 && <span className="text-slate-500"> · {aircraft.length}</span>}
      </h3>
      {aircraft === null ? (
        <p className="text-sm text-slate-500">Scanning ADS-B…</p>
      ) : (
        <ul className="space-y-1.5" data-testid="live-aircraft">
          {aircraft.map((ac) => (
            <li key={ac.id}>
              <button
                type="button"
                onClick={() => openAircraft(ac.id)}
                className={`w-full flex items-center gap-2 text-left p-1.5 rounded border ${
                  ac.is_water_bomber
                    ? 'bg-orange-500/10 border-orange-500/40 hover:bg-orange-500/20'
                    : 'bg-slate-800/60 border-slate-700 hover:bg-slate-700'
                }`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-xs truncate">
                    {ac.callsign ?? ac.registration}
                    {ac.is_water_bomber && (
                      <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-orange-500/30 text-orange-200">
                        water bomber
                      </span>
                    )}
                  </span>
                  <span className="block text-[10px] text-slate-400">
                    {ac.type} · {ac.distance_km.toFixed(1)} km
                    {ac.altitude_ft != null ? ` · ${ac.altitude_ft.toLocaleString()} ft` : ''}
                  </span>
                </span>
                <span className="text-[10px] text-slate-500 flex-shrink-0">
                  {ac.last_seen_seconds_ago < 60
                    ? `${ac.last_seen_seconds_ago}s ago`
                    : `${Math.floor(ac.last_seen_seconds_ago / 60)}m ago`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** #485 — the promote reopen-NUDGE proximity radius (mirrors the protective-action
 *  AUTO_LINK_RADIUS_M) and its recently-deactivated lookback window. */
const REOPEN_NUDGE_RADIUS_M = 25_000;
const REOPEN_NUDGE_WINDOW_DAYS = 30;

/** Great-circle distance (metres) — for the "within ~25 km" nudge filter (the bbox query is a
 *  square, so we refine to a true radius client-side). */
function haversineM(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6_371_000;
  const toRad = (d: number): number => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

/**
 * Own Incident affordance (#121; doctrine rename #486 — "Own", not "Declare", per
 * docs/ontario-ims-doctrine-reference.md) — shown in the SITL slot of an incident-
 * capable panel (high-risk hotspot, reported fire) when no tracked incident has
 * resolved yet. One click promotes the source record through the existing
 * idempotent POST /api/incidents path (server audits `promote_incident`) and
 * hands the new incident back so the IncidentManagementSection renders in place.
 * Confirm Fire is untouched — owning is a distinct, explicit operator action.
 *
 * #485 REOPEN-NUDGE (MUST-1b) — before minting a NEW incident, if a same-hazard incident was
 * recently DEACTIVATED within ~25 km, offer "Reopen <number> instead?" so a re-emergence reopens
 * the whole thread rather than losing it to a duplicate. A NUDGE, not a block: Own Incident stays.
 * A stale reopen (older than the founder threshold) carries the two-step staleness warning.
 */
function DeclareIncidentButton({
  name,
  hazardType = 'wildfire',
  sourceRecordType,
  sourceRecordId,
  lon,
  lat,
  onDeclared,
}: {
  name: string;
  hazardType?: string;
  sourceRecordType: IncidentSourceRecordType;
  sourceRecordId: string;
  lon: number;
  lat: number;
  onDeclared: (incident: Incident) => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  // #485 — the nearest same-hazard recently-deactivated incident within radius (the nudge), the
  // reopen-in-flight flag, and the stale-reopen two-step confirm.
  const [nudge, setNudge] = useState<Incident | null>(null);
  const [reopening, setReopening] = useState(false);
  const [staleConfirm, setStaleConfirm] = useState(false);

  useEffect(() => {
    const ctl = new AbortController();
    // The nudge target changed (a different source selected) — reset the two-step stale
    // confirm + any in-flight reopen so a half-confirmed stale reopen from the PREVIOUS
    // source can never bypass the next source's own warning (DeclareIncidentButton is not
    // keyed by source).
    setStaleConfirm(false);
    setReopening(false);
    const latSpan = REOPEN_NUDGE_RADIUS_M / 111_320;
    // The E-W half-span in degrees for the ~25 km box. cos(lat) floored at a small epsilon so
    // the box NEVER narrows below the radius at far-north latitudes (a narrower box could miss
    // an in-radius candidate); the haversine filter below refines to a true 25 km circle.
    const lonSpan =
      REOPEN_NUDGE_RADIUS_M / (111_320 * Math.max(Math.abs(Math.cos((lat * Math.PI) / 180)), 0.02));
    void fetchIncidents(
      {
        status: 'closed',
        lifecycle_state: 'deactivated',
        deactivated_within_days: REOPEN_NUDGE_WINDOW_DAYS,
        bbox: [lon - lonSpan, lat - latSpan, lon + lonSpan, lat + latSpan],
        limit: 20,
      },
      ctl.signal,
    )
      .then((list) => {
        const nearest = list
          .filter((i) => i.hazard_type === hazardType)
          .map((i) => ({ i, d: haversineM(lat, lon, i.lat, i.lon) }))
          .filter((x) => x.d <= REOPEN_NUDGE_RADIUS_M)
          .sort((a, b) => a.d - b.d)[0];
        setNudge(nearest?.i ?? null);
      })
      .catch(() => setNudge(null));
    return () => ctl.abort();
  }, [lon, lat, hazardType]);

  const nudgeDays =
    nudge?.closed_at != null
      ? Math.floor((Date.now() - new Date(nudge.closed_at).getTime()) / 86_400_000)
      : 0;
  const nudgeIsStale = nudgeDays > REACTIVATION_STALENESS_WARNING_DAYS;

  const declare = (): void => {
    if (busy) return;
    setBusy(true);
    void createIncident({
      session_id: getSessionId(),
      name,
      hazard_type: hazardType,
      source_record_type: sourceRecordType,
      source_record_id: sourceRecordId,
      lon,
      lat,
    })
      .then((inc) => {
        showToast(ownIncidentToast(inc.incident_number), { variant: 'success' });
        onDeclared(inc);
      })
      .catch(() => {
        showToast(OWN_INCIDENT_ERROR, { variant: 'error' });
      })
      .finally(() => setBusy(false));
  };

  const reopen = (): void => {
    if (!nudge || reopening) return;
    // Stale reopen ⇒ show the warning first (two-step); the second click proceeds.
    if (nudgeIsStale && !staleConfirm) {
      setStaleConfirm(true);
      return;
    }
    setReopening(true);
    void advanceIncidentLifecycle(nudge.id, 'confirmed', getSessionId())
      .then((inc) => {
        showToast(`${REACTIVATE_INCIDENT_LABEL}d ${inc.incident_number}.`, { variant: 'success' });
        onDeclared(inc);
      })
      .catch((err) =>
        showToast((err as Error).message || 'Could not reactivate.', { variant: 'error' }),
      )
      .finally(() => setReopening(false));
  };

  return (
    <section
      data-testid="declare-incident"
      className="space-y-2 border-t border-slate-700/70 pt-4"
      aria-label={OWN_INCIDENT_ARIA_LABEL}
    >
      <p className="text-[10px] uppercase tracking-wider text-slate-500">Tracked Incident</p>
      {/* #485 REOPEN-NUDGE — offered BEFORE minting a new incident when a recently-deactivated
          same-hazard incident is within ~25 km. A nudge, not a block: Own Incident stays below. */}
      {nudge && (
        <div
          data-testid="reopen-nudge"
          className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 space-y-1.5"
        >
          <p className="text-[12px] font-semibold text-amber-100" data-testid="reopen-nudge-prompt">
            {reopenInsteadPrompt(nudge.incident_number)}
          </p>
          <p className="text-[11px] leading-snug text-amber-200/80">{REOPEN_NUDGE_HELP}</p>
          {staleConfirm && (
            <p
              data-testid="reopen-nudge-stale-warning"
              className="text-[11px] leading-snug text-amber-300"
            >
              {staleReactivationWarning(nudgeDays)}
            </p>
          )}
          <button
            type="button"
            data-testid="reopen-nudge-reactivate"
            disabled={reopening}
            onClick={reopen}
            className="w-full text-[12px] py-1.5 rounded bg-amber-600/90 hover:bg-amber-600 border border-amber-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
          >
            {reopening
              ? `${REACTIVATE_INCIDENT_LABEL}…`
              : staleConfirm
                ? `${REACTIVATE_INCIDENT_LABEL} anyway`
                : `${REACTIVATE_INCIDENT_LABEL} ${nudge.incident_number}`}
          </button>
        </div>
      )}
      <button
        type="button"
        data-testid="declare-incident-btn"
        disabled={busy}
        onClick={declare}
        className="w-full text-sm py-1.5 rounded bg-indigo-600/80 hover:bg-indigo-600 border border-indigo-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
      >
        {busy ? OWN_INCIDENT_BUSY_LABEL : OWN_INCIDENT_LABEL}
      </button>
      <p className="text-[10px] text-slate-500 leading-relaxed">{OWN_INCIDENT_HELP}</p>
    </section>
  );
}

/**
 * Dispatch asset picker (#122) — a DestinationPicker-idiom <select> over the
 * Nearby Response Resources list. Default (empty) = the server's nearest-of-
 * priority pick, so the one-click Recommend Dispatch flow is preserved; choosing
 * a listed resource sends its id, and the server binds the resource request to
 * it. Self-fetches the same in-range list the Nearby Response Resources section
 * shows; renders nothing when no resources are in range (pure one-click).
 */
function DispatchAssetPicker({
  lat,
  lon,
  value,
  onChange,
  disabled,
  testid,
}: {
  lat: number;
  lon: number;
  /** Chosen asset id; undefined = auto/nearest (server picks). */
  value: string | undefined;
  onChange: (assetId: string | undefined) => void;
  disabled?: boolean;
  testid?: string;
}): JSX.Element | null {
  const [assets, setAssets] = useState<NearbyAsset[]>([]);

  useEffect(() => {
    const ctl = new AbortController();
    void fetchNearbyAssets(lat, lon, ctl.signal, { radiusKm: 50, limit: 10 })
      .then(setAssets)
      .catch(() => setAssets([]));
    return () => ctl.abort();
  }, [lat, lon]);

  if (assets.length === 0) return null;

  return (
    <label className="flex items-center gap-1.5 text-[11px] text-slate-400">
      <span className="uppercase tracking-wide whitespace-nowrap">Dispatch</span>
      <select
        data-testid={testid ?? 'dispatch-asset-picker'}
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value || undefined)}
        className="min-w-0 flex-1 text-xs py-1 px-2 rounded bg-slate-800 border border-slate-600 text-slate-100 disabled:opacity-50"
      >
        <option value="">Nearest available (recommended)</option>
        {assets.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name} — {a.distance_km.toFixed(1)} km {a.bearing}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Dispatch actions (R.3.6) — the "Confirm Fire" + "Recommend Dispatch" buttons.
 * Each POSTs a dispatch_recommendation (server snapshots the incident + nearest
 * resources), shows a toast on success, and logs the click to the operator-action
 * audit trail. A created row is 'pending'; the operator then takes a SECOND,
 * distinct action — "Forward to Agency" — to send it through the real §4.21
 * delivery channel (migration 0047). The system never auto-sends.
 */
function DispatchActions({
  incidentType,
  incidentId,
  lat,
  lon,
  hazardNoun = 'Wildfire',
  onResourceRequestCreated,
}: {
  incidentType: IncidentType;
  incidentId: string;
  /** Incident point — used by the #122 dispatch asset picker to list nearby
   *  resources the operator can choose as the dispatch target. */
  lat: number;
  lon: number;
  /** #335 — operator-facing hazard NOUN driving the confirm label + toast ("Wildfire"
   *  / "Flood" / "Ice Storm" / "Hazard"). Derived from declareHazardType so a non-fire
   *  hazard never inherits a fire word. Backend recommendation_type stays 'confirm_fire'. */
  hazardNoun?: string;
  /** Fired after a 'recommend_dispatch' raises a resource_request (R.4.1). The
   *  created recommendation is passed so callers can read promoted_incident_id
   *  (Task #92 — the active_fire's auto-promoted incident). */
  onResourceRequestCreated?: (rec: DispatchRecommendation) => void;
}): JSX.Element {
  const [busy, setBusy] = useState<RecommendationType | null>(null);
  // #122 — operator-chosen dispatch target; undefined = server's nearest pick.
  const [selectedAssetId, setSelectedAssetId] = useState<string | undefined>(undefined);
  // The just-created recommendation, surfaced as a forwardable record. Local-only
  // (the canonical row lives server-side); replaced each time a new rec is made.
  const [rec, setRec] = useState<DispatchRecommendation | null>(null);
  const [forwarding, setForwarding] = useState(false);
  const [destinationId, setDestinationId] = useState<string | undefined>(undefined);
  const [deliveryRef, setDeliveryRef] = useState<string | null>(null);
  // #379 — the dominant down-tasking control: issue an agency_task to a NON-SELF agency.
  const [taskAgencyId, setTaskAgencyId] = useState<string | undefined>(undefined);
  const [tasking, setTasking] = useState(false);
  const [taskedId, setTaskedId] = useState<string | null>(null);

  const fire = (recommendationType: RecommendationType): void => {
    if (busy) return;
    setBusy(recommendationType);
    logAction(
      recommendationType === 'confirm_fire' ? 'confirm_fire_clicked' : 'recommend_dispatch_clicked',
      { incident_type: incidentType, incident_id: incidentId },
    );
    void createDispatchRecommendation({
      incident_type: incidentType,
      incident_id: incidentId,
      recommendation_type: recommendationType,
      session_id: getSessionId(),
      // #122 — only the dispatch leg binds a resource; confirm_fire ignores it.
      asset_id: recommendationType === 'recommend_dispatch' ? selectedAssetId : undefined,
    })
      .then((created) => {
        // Surface the new recommendation as a pending, forwardable record.
        setRec(created);
        setDeliveryRef(null);
        if (recommendationType === 'confirm_fire') {
          showToast(
            `${hazardNoun} confirmed — dispatch recommendation prepared. Status: pending review.`,
            {
              variant: 'success',
            },
          );
        } else {
          const top = created.recommended_assets[0];
          showToast(
            top
              ? `Dispatch recommended: ${top.asset_name} (~${top.eta_minutes} min ETA). Resource request raised — status: pending.`
              : 'Dispatch recommendation prepared (no resources in range). Resource request raised — status: pending.',
            { variant: 'success' },
          );
          // R.4.1 — a resource_request was raised; refresh the sub-section.
          if (created.resource_request_id) onResourceRequestCreated?.(created);
        }
      })
      .catch(() => {
        showToast('Could not prepare the dispatch recommendation. Please retry.', {
          variant: 'error',
        });
      })
      .finally(() => setBusy(null));
  };

  const forward = (): void => {
    if (!rec || forwarding || rec.status === 'forwarded') return;
    setForwarding(true);
    void forwardDispatchRecommendation(rec.id, getSessionId(), destinationId)
      .then((delivery) => {
        setRec((r) => (r ? { ...r, status: 'forwarded' } : r));
        setDeliveryRef(delivery.delivery_log_id);
        const dest = delivery.destination?.name ?? FORWARD_RECIPIENT_LABEL;
        showToast(
          `Forwarded to ${dest} via ${delivery.delivery_channel} — delivery ref ${delivery.delivery_log_id}.`,
          { variant: 'success' },
        );
      })
      .catch(() => {
        showToast('Could not forward the recommendation. Please retry.', { variant: 'error' });
      })
      .finally(() => setForwarding(false));
  };

  // #379 — issue an agency_task DOWN to the picked NON-SELF agency, carrying this
  // recommendation + its raised resource_request. The dominant control; the legacy
  // forward-to-a-delivery-destination stays available but secondary.
  const taskAgency = (): void => {
    if (!rec || tasking || !taskAgencyId) return;
    setTasking(true);
    void taskAgencyFromDispatch(rec.id, {
      target_agency_id: taskAgencyId,
      resource_request_id: rec.resource_request_id ?? null,
      session_id: getSessionId(),
    })
      .then((res) => {
        setTaskedId(res.agency_task_id);
        showToast('Tasked the agency — tracked in Coordination → Tasks.', { variant: 'success' });
      })
      .catch(() => {
        showToast('Could not task the agency. Please retry.', { variant: 'error' });
      })
      .finally(() => setTasking(false));
  };

  return (
    <section className="space-y-2" aria-label="Response actions">
      <button
        type="button"
        data-testid="confirm-fire-btn"
        disabled={busy !== null}
        onClick={() => fire('confirm_fire')}
        className="w-full text-sm py-1.5 rounded bg-rose-600/80 hover:bg-rose-600 border border-rose-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
      >
        {busy === 'confirm_fire' ? 'Confirming…' : `Confirm ${hazardNoun}`}
      </button>
      {/* #122 — pick the dispatch target (defaults to nearest, so one-click holds). */}
      <DispatchAssetPicker
        lat={lat}
        lon={lon}
        value={selectedAssetId}
        onChange={setSelectedAssetId}
        disabled={busy !== null}
      />
      <button
        type="button"
        data-testid="recommend-dispatch-btn"
        disabled={busy !== null}
        onClick={() => fire('recommend_dispatch')}
        className="w-full text-sm py-1.5 rounded bg-sky-600/80 hover:bg-sky-600 border border-sky-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
      >
        {busy === 'recommend_dispatch' ? 'Preparing…' : 'Recommend Dispatch'}
      </button>

      {/* The created recommendation, as a pending → forwarded record. The forward
          is a SECOND, distinct operator action (recommend → forward); never auto-sent. */}
      {rec && (
        <div
          data-testid="dispatch-recommendation-record"
          className="mt-1 rounded border border-slate-700 bg-slate-800/40 p-2 space-y-1.5"
        >
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-slate-400">Dispatch recommendation</span>
            <span
              data-testid="dispatch-rec-status"
              className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                rec.status === 'forwarded'
                  ? 'bg-emerald-700/60 text-emerald-200'
                  : 'bg-amber-700/50 text-amber-200'
              }`}
            >
              {rec.status}
            </span>
          </div>
          {rec.status === 'forwarded' ? (
            <p data-testid="dispatch-forwarded-note" className="text-[10px] text-emerald-300/90 leading-relaxed">
              Forwarded to {FORWARD_RECIPIENT_LABEL}
              {deliveryRef ? ` — delivery ref ${deliveryRef}` : ''}.
            </p>
          ) : (
            <div className="space-y-2">
              {/* PRIMARY (#379): task a NAMED, NON-SELF agency. The dominant down-tasking
                  control — replaces forward-to-self with a deliberate agency pick. */}
              {taskedId ? (
                <p
                  data-testid="dispatch-tasked-note"
                  className="text-[10px] text-amber-300/90 leading-relaxed"
                >
                  Tasked a named agency — tracked in Coordination → Tasks.
                </p>
              ) : (
                <div className="space-y-1.5">
                  <AgencyPicker
                    value={taskAgencyId}
                    onChange={setTaskAgencyId}
                    disabled={tasking}
                    testid="dispatch-agency-picker"
                  />
                  <button
                    type="button"
                    data-testid="task-agency-dispatch-btn"
                    disabled={tasking || !taskAgencyId}
                    onClick={taskAgency}
                    className="w-full text-sm py-1.5 rounded bg-amber-600/80 hover:bg-amber-600 border border-amber-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
                  >
                    {tasking ? 'Tasking…' : 'Task agency'}
                  </button>
                </div>
              )}
              {/* SECONDARY (legacy, kept): forward to a delivery destination. No longer
                  the default — folded behind a disclosure so the primary control leads. */}
              <details className="text-[10px] text-slate-400">
                <summary className="cursor-pointer select-none hover:text-slate-200">
                  Forward to a delivery destination (legacy)
                </summary>
                <div className="space-y-1.5 mt-1.5">
                  <DestinationPicker
                    value={destinationId}
                    onChange={setDestinationId}
                    disabled={forwarding}
                    testid="dispatch-destination-picker"
                  />
                  <button
                    type="button"
                    data-testid="forward-dispatch-btn"
                    disabled={forwarding}
                    onClick={forward}
                    className="w-full text-sm py-1.5 rounded bg-emerald-600/80 hover:bg-emerald-600 border border-emerald-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
                  >
                    {forwarding ? 'Forwarding…' : 'Forward to Agency'}
                  </button>
                </div>
              </details>
            </div>
          )}
        </div>
      )}

      <p className="text-[10px] text-slate-500 leading-relaxed">
        Recommend → task a named agency. A recommendation is logged for review; nothing is
        dispatched automatically (no CAD write). Tasking an agency issues an acknowledgeable
        down-task — a separate, deliberate action; the system never auto-sends.
      </p>
    </section>
  );
}

/** Label for the dispatch-forward recipient (mirrors the server FORWARD_RECIPIENT). */
const FORWARD_RECIPIENT_LABEL = 'responding agency';

/** The full 7-state IMS Resource Status Lifecycle (R.4.2; §6.4 decision (a)).
 *  The stepper component is parameterized — the 4-state Assistance Request reuse
 *  still passes its own labels; this section drives all 7. */
const RESOURCE_STEP_LABELS = [
  'Available',
  'Requested',
  'Assigned',
  'Checked In',
  'Staging',
  'Committed',
  'Demobilized',
] as const;

/** request_status → the index reached in the 7-state lifecycle. */
const REACHED_INDEX: Record<string, number> = {
  pending: 1, // left 'available', now 'requested'
  assigned: 2,
  checked_in: 3,
  staging: 4,
  committed: 5,
  fulfilled: 5,
  demobilized: 6,
  cancelled: -1,
};

function fmtRrShort(iso: string | null): string | null {
  if (!iso) return null;
  return iso.length >= 16 ? iso.slice(5, 16).replace('T', ' ') : iso;
}

/** Build the 7-state stepper steps from one request's status + timestamps. */
function buildResourceSteps(r: ResourceRequestDetailLite): LifecycleStep[] {
  const reached = REACHED_INDEX[r.request_status] ?? 0;
  const cancelled = r.request_status === 'cancelled';
  const stamps: Array<string | null> = [
    r.requested_at, // Available → Requested transition stamp
    r.requested_at,
    r.assigned_at,
    r.checked_in_at,
    r.staging_at,
    r.committed_at,
    r.demobilized_at,
  ];
  // Staging step surfaces the bound area on hover.
  const details: Array<string | null> = [null, null, null, null, r.staging_area_name, null, null];
  return RESOURCE_STEP_LABELS.map((label, i) => {
    const status: LifecycleStep['status'] = cancelled
      ? stamps[i]
        ? 'complete'
        : 'future'
      : i < reached
        ? 'complete'
        : i === reached
          ? 'current'
          : 'future';
    return { label, status, timestamp: fmtRrShort(stamps[i] ?? null), detail: details[i] };
  });
}

/** Minimal request shape the stepper + row need (list item + fetched stamps). */
interface ResourceRequestDetailLite {
  id: string;
  request_number: string;
  request_status: string;
  urgency: string;
  incident_id: string | null;
  requested_at: string;
  assigned_at: string | null;
  checked_in_at: string | null;
  staging_at: string | null;
  committed_at: string | null;
  demobilized_at: string | null;
  staging_area_id: string | null;
  staging_area_name: string | null;
  asset_name: string | null;
  requested_class: string | null;
}

/** The forward "next" lifecycle action available from a given request status. */
type RrNext =
  | { action: 'assign'; label: string }
  | { action: 'check-in'; label: string }
  | { action: 'stage'; label: string }
  | { action: 'commit'; label: string }
  | { action: 'demobilize'; label: string }
  | null;

function nextResourceAction(status: string): RrNext {
  switch (status) {
    case 'pending':
      return { action: 'assign', label: 'Assign →' };
    case 'assigned':
      return { action: 'check-in', label: 'Check In →' };
    case 'checked_in':
      return { action: 'stage', label: 'Stage →' };
    case 'staging':
      return { action: 'commit', label: 'Commit →' };
    case 'committed':
      return { action: 'demobilize', label: 'Demobilize' };
    default:
      return null;
  }
}

/** Inline staging-area picker shown when an operator stages a checked-in request.
 *  Lists the request's incident's open staging areas + an inline declare form
 *  (placed at the hotspot location). Calls onStage(areaId) once one is chosen. */
function StageControl({
  incidentId,
  declareLat,
  declareLon,
  busy,
  onStage,
  onCancel,
}: {
  incidentId: string | null;
  declareLat: number;
  declareLon: number;
  busy: boolean;
  onStage: (stagingAreaId: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const [areas, setAreas] = useState<StagingArea[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [declaring, setDeclaring] = useState(false);
  const [newName, setNewName] = useState('');
  const [working, setWorking] = useState(false);

  const reloadAreas = (): void => {
    if (!incidentId) {
      setLoaded(true);
      return;
    }
    void fetchStagingAreas({ incident_id: incidentId })
      .then((res) => setAreas(res.staging_areas))
      .catch(() => {})
      .finally(() => setLoaded(true));
  };

  useEffect(reloadAreas, [incidentId]);

  if (!incidentId) {
    return (
      <div className="text-[10px] text-amber-300/80 rounded bg-amber-900/20 border border-amber-800/40 px-2 py-1">
        Staging needs an incident-linked request. Own this hotspot as an incident first.
        <button
          type="button"
          onClick={onCancel}
          className="ml-2 underline text-slate-400 hover:text-slate-200"
        >
          dismiss
        </button>
      </div>
    );
  }

  const declare = (): void => {
    if (!newName.trim() || working) return;
    setWorking(true);
    void createStagingArea({
      session_id: getSessionId(),
      incident_id: incidentId,
      name: newName.trim(),
      lat: declareLat,
      lon: declareLon,
    })
      .then((area) => {
        setNewName('');
        setDeclaring(false);
        setAreas((prev) => [area, ...prev]);
        showToast(`Staging area "${area.name}" established.`, { variant: 'success' });
        onStage(area.id);
      })
      .catch(() => showToast('Could not establish staging area.', { variant: 'error' }))
      .finally(() => setWorking(false));
  };

  return (
    <div
      data-testid="stage-control"
      className="rounded border border-sky-800/50 bg-sky-950/30 p-2 space-y-1.5"
    >
      <div className="text-[10px] uppercase tracking-wide text-sky-300/80">Stage into…</div>
      {loaded && areas.length > 0 && (
        <ul className="space-y-1">
          {areas.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                data-testid={`stage-into-${a.id}`}
                disabled={busy}
                onClick={() => onStage(a.id)}
                className="w-full text-left text-[11px] py-1 px-2 rounded bg-sky-800/40 hover:bg-sky-700/60 border border-sky-700 text-sky-100 disabled:opacity-50"
              >
                {a.name}
                {a.assigned_request_count != null && (
                  <span className="text-[9px] text-sky-400/70"> · {a.assigned_request_count} here</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {loaded && areas.length === 0 && !declaring && (
        <div className="text-[10px] text-slate-400">No open staging areas for this incident yet.</div>
      )}
      {declaring ? (
        <div className="flex items-center gap-1">
          <input
            data-testid="stage-new-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Cochrane FOB"
            className="flex-1 min-w-0 text-[11px] py-1 px-1.5 rounded bg-slate-900 border border-slate-700 text-slate-100"
          />
          <button
            type="button"
            data-testid="stage-declare-confirm"
            disabled={working || !newName.trim()}
            onClick={declare}
            className="text-[11px] py-1 px-2 rounded bg-emerald-600/80 hover:bg-emerald-600 border border-emerald-500 text-white disabled:opacity-50"
          >
            Establish + Stage
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-testid="stage-declare-open"
          onClick={() => setDeclaring(true)}
          className="text-[10px] underline text-sky-300 hover:text-sky-200"
        >
          + Establish new staging area (at this hotspot)
        </button>
      )}
      <button
        type="button"
        onClick={onCancel}
        className="block text-[10px] underline text-slate-400 hover:text-slate-200"
      >
        cancel
      </button>
    </div>
  );
}

/** One resource-request row: 7-state stepper + the forward transition + cancel,
 *  with the staging picker inlined when staging a checked-in request. */
function ResourceRequestRow({
  r,
  busy,
  declareLat,
  declareLon,
  onTransition,
}: {
  r: ResourceRequestListItem;
  busy: boolean;
  declareLat: number;
  declareLon: number;
  onTransition: (
    r: ResourceRequestListItem,
    action: 'assign' | 'check-in' | 'stage' | 'commit' | 'demobilize' | 'cancel',
    extra?: { staging_area_id?: string },
  ) => void;
}): JSX.Element {
  const [staging, setStaging] = useState(false);
  const steps = buildResourceSteps(r as unknown as ResourceRequestDetailLite);
  const next = nextResourceAction(r.request_status);
  const isCommitted = r.request_status === 'committed';

  return (
    <div
      data-testid={`resource-request-${r.request_number}`}
      className="rounded border border-slate-700 bg-slate-800/40 p-2 space-y-2"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium text-slate-200">{r.request_number}</span>
        <span className="text-[10px] text-slate-400 truncate">
          {r.asset_name ?? r.requested_class ?? 'resource'}
        </span>
      </div>
      <LifecycleStepper
        data-testid={`resource-stepper-${r.request_number}`}
        steps={steps}
        urgency={r.urgency as 'routine' | 'urgent' | 'immediate'}
        onTransition={
          next
            ? () => (next.action === 'stage' ? setStaging(true) : onTransition(r, next.action))
            : undefined
        }
      />
      {r.staging_area_name && (
        <div
          data-testid={`resource-staging-area-${r.request_number}`}
          className="text-[10px] text-sky-300/80"
        >
          ▲ Staged at {r.staging_area_name}
        </div>
      )}
      {staging && next?.action === 'stage' ? (
        <StageControl
          incidentId={r.incident_id}
          declareLat={declareLat}
          declareLon={declareLon}
          busy={busy}
          onStage={(stagingAreaId) => {
            setStaging(false);
            onTransition(r, 'stage', { staging_area_id: stagingAreaId });
          }}
          onCancel={() => setStaging(false)}
        />
      ) : (
        <div className="flex items-center gap-2">
          {next && (
            <button
              type="button"
              data-testid={`resource-advance-btn-${r.request_number}`}
              disabled={busy}
              onClick={() =>
                next.action === 'stage' ? setStaging(true) : onTransition(r, next.action)
              }
              className={`text-[11px] py-1 px-2 rounded border font-medium text-white disabled:opacity-50 ${
                isCommitted
                  ? 'bg-slate-600/80 hover:bg-slate-600 border-slate-500'
                  : 'bg-emerald-600/80 hover:bg-emerald-600 border-emerald-500'
              }`}
            >
              {next.label}
            </button>
          )}
          {!isCommitted && (
            <button
              type="button"
              data-testid={`resource-cancel-btn-${r.request_number}`}
              disabled={busy}
              onClick={() => onTransition(r, 'cancel')}
              className="text-[11px] py-1 px-2 rounded bg-rose-700/60 hover:bg-rose-700 border border-rose-600 text-rose-100 disabled:opacity-50"
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * R.4.1 → R.4.2 — "Resource Requests" sub-section under the R.3 action buttons.
 * Lists the active (pending → committed) requests raised for this incident, each
 * rendered as the full 7-state lifecycle stepper with the forward transitions
 * (assign → check-in → stage → commit → demobilize) + cancel. Keyed by the FK
 * R.3 "Recommend Dispatch" sets: a high_risk_hotspot links via `hotspot_id`; an
 * active_fire (Task #92) is auto-promoted to a tracked incident and links via
 * `incident_id`. Staging binds a request to a staging area on its incident.
 */
function ResourceRequestsSection({
  hotspotId,
  incidentId,
  reloadToken,
  lat,
  lon,
}: {
  hotspotId?: string;
  incidentId?: string;
  reloadToken: number;
  lat: number;
  lon: number;
}): JSX.Element | null {
  const [rows, setRows] = useState<ResourceRequestListItem[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // One key drives the query — hotspot for a fused hotspot, incident for a
  // promoted active_fire. Nothing to fetch until at least one is present.
  const filter = hotspotId
    ? { hotspot_id: hotspotId, limit: 25 }
    : incidentId
      ? { incident_id: incidentId, limit: 25 }
      : null;

  const reload = (): void => {
    if (!filter) return;
    void fetchResourceRequests(filter)
      .then((res) => setRows(res.requests))
      .catch(() => {
        /* non-fatal — leave the list as-is */
      })
      .finally(() => setLoaded(true));
  };

  useEffect(() => {
    if (!filter) {
      setRows([]);
      setLoaded(true);
      return;
    }
    let cancelled = false;
    void fetchResourceRequests(filter)
      .then((res) => {
        if (!cancelled) setRows(res.requests);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotspotId, incidentId, reloadToken]);

  // Active = everything that isn't a terminal state (cancelled/fulfilled/demobilized).
  const open = rows.filter((r) =>
    ['pending', 'assigned', 'checked_in', 'staging', 'committed'].includes(r.request_status),
  );
  if (!loaded || open.length === 0) return null;

  const handleTransition = (
    r: ResourceRequestListItem,
    action: 'assign' | 'check-in' | 'stage' | 'commit' | 'demobilize' | 'cancel',
    extra?: { staging_area_id?: string },
  ): void => {
    if (busyId) return;
    setBusyId(r.id);
    void transitionResourceRequest(r.id, action, { session_id: getSessionId(), ...extra })
      .then(() => {
        const verb =
          action === 'cancel'
            ? 'cancelled'
            : action === 'check-in'
              ? 'checked in'
              : action === 'stage'
                ? 'staged'
                : action === 'demobilize'
                  ? 'demobilized'
                  : `${action}ed`;
        showToast(`${r.request_number} → ${verb}.`, { variant: 'success' });
        reload();
      })
      .catch(() => showToast(`Could not ${action} ${r.request_number}.`, { variant: 'error' }))
      .finally(() => setBusyId(null));
  };

  return (
    <section
      className="space-y-2"
      aria-label="Resource requests"
      data-testid="resource-requests-section"
    >
      <div className="text-[10px] uppercase tracking-wide text-slate-500">
        Resource Requests ({open.length})
      </div>
      {open.map((r) => (
        <ResourceRequestRow
          key={r.id}
          r={r}
          busy={busyId === r.id}
          declareLat={lat}
          declareLon={lon}
          onTransition={handleTransition}
        />
      ))}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * IncidentWorkSurface (Coordination re-arch Phase 1 — IMS Ruling 1).
 *
 * The ONE reusable "work the incident" action set. Per the binding council
 * (docs/coordination-rearchitecture-council-2026-06.md), owning an alert makes it
 * the tracked incident and the operator works it FROM one place with the full
 * action set — not the SITREP chain on one surface and the actions on another.
 *
 * Rendered in TWO places, never copy-pasted:
 *   • the alert detail (HighRiskHotspotBody) — bound to the hotspot; offers the
 *     one-click Declare when not yet owned, then the management section once owned.
 *   • the incident detail (the {kind:'incident'} SidePanel arm) — bound to the
 *     incident's SOURCE alert, so the operator confirms / recommends dispatch /
 *     requests a drone / stages resources / advances + closes WITHOUT flying to
 *     the alert.
 *
 * `source` carries the alert-derived binding (record type + id + centroid). The
 * alert-derived actions bind to the SOURCE RECORD id (the hotspot/fire id), exactly
 * as on the alert; the incident-level actions bind to `incidentId`. When `source`
 * is null (a manually-declared incident with no source alert), ONLY the incident-
 * level actions render — there is no source to confirm / dispatch / stage against.
 * ------------------------------------------------------------------ */

/** Source record type → the dispatch IncidentType (the Confirm-Fire / Recommend-
 *  Dispatch + nearby-assets audit binding). A CAP-sourced incident is not
 *  dispatchable via the R.3 schema, so it resolves to null (no dispatch buttons). */
const SOURCE_TO_INCIDENT_TYPE: Partial<Record<IncidentSourceRecordType, IncidentType>> = {
  high_risk_hotspot: 'high_risk_hotspot',
  active_wildland_fire: 'active_fire',
};

/** Source record type → the Connected-ASSIST alert kind (#185 cross-link). Only the
 *  two cross-linkable sources resolve; the rest render no link (contextual gate). */
const SOURCE_TO_ALERT_KIND: Partial<Record<IncidentSourceRecordType, AlertSelectionKind>> = {
  high_risk_hotspot: 'highRiskHotspot',
  cap_alert: 'capAlert',
};

/** #443 — source hazards whose Nearby-cameras list defaults to 'all-nearby' (every live
 *  camera in range, distance-ranked) rather than the fire smoke-priority + line-of-sight
 *  subset. A flood / ice / CAP weather AREA wants road, river-gauge and community cams;
 *  a pinpoint wildfire/hotspot wants smoke-visible cameras. Fire sources fall through to
 *  the 'fire' default, so the wildfire path stays byte-unchanged. */
const NON_FIRE_CAMERA_SOURCE_TYPES: ReadonlySet<IncidentSourceRecordType> = new Set([
  'cap_alert',
  'flood_anomaly',
  'ice_storm_anomaly',
]);

export interface IncidentWorkSurfaceSource {
  /** The provenance record the incident promotes from. */
  recordType: IncidentSourceRecordType;
  /** The source record id — the alert-derived actions bind to THIS (the hotspot/
   *  fire id), exactly as on the alert detail. */
  recordId: string;
  lat: number;
  lon: number;
  /** Drone "Request live eyes" area-of-interest label ("this hotspot" / "this fire"). */
  aoLabel: string;
  /** Default name + hazard for the Declare affordance (alert side, pre-ownership). */
  declareName: string;
  declareHazardType?: string;
}

export function IncidentWorkSurface({
  source,
  incidentId,
  lat,
  lon,
  onIncidentDeclared,
  onIncidentPromoted,
  onSelectCamera,
  onSelectAsset,
  onSelectAircraft,
  onOpenSitrep,
  onOpenConnectedAssist,
  onOpenReplay,
  onOpenEvacuation,
  onSelectMission,
  replayActive = false,
  cameraTuning,
}: {
  /** The source alert/record the incident is (or would be) anchored on. null = a
   *  manually-declared incident → incident-level actions only. */
  source: IncidentWorkSurfaceSource | null;
  /** The resolved tracked incident id, when owned (drives the IMS section + the
   *  drone-on-incident binding). null on an unowned alert → the Declare affordance. */
  incidentId: string | null;
  /** The incident/alert centroid — used by the incident-scoped resource thread
   *  (staging declare point) when there is no `source` to read it from. Falls back
   *  to `source` when present. */
  lat?: number;
  lon?: number;
  /** Alert side: the operator just Declared — adopt the new incident (the caller
   *  owns the incidentId state so the surface re-renders into the management view). */
  onIncidentDeclared?: (incident: Incident) => void;
  /** Coordination re-arch Phase 2 — Recommend Dispatch on an unowned source the
   *  server auto-promotes (Task #92, active_fire) returns a promoted_incident_id;
   *  the caller adopts it so the management section + resource thread appear. */
  onIncidentPromoted?: (incidentId: string) => void;
  onSelectCamera?: (c: CameraFeature) => void;
  onSelectAsset?: (a: ResponseAssetFeature) => void;
  onSelectAircraft?: (a: AircraftFeature) => void;
  onOpenSitrep?: (id: string) => void;
  onOpenConnectedAssist?: (requestId: string) => void;
  onOpenReplay?: (incidentId: string) => void;
  /** §4.30 — open the incident-scoped Evacuation Logistics panel. The entry is
   *  data-driven (≥1 linked community); pass this to surface it on this incident. */
  onOpenEvacuation?: (incidentId: string) => void;
  /** Phase 4e — open a drone's {kind:'mission'} detail from the "drones on this incident" list,
   *  closing the Situation-Unit live-feed dead-end (§I.2). Threaded into IncidentDronesPanel. */
  onSelectMission?: (m: DroneMissionView) => void;
  replayActive?: boolean;
  /** #443 — camera tuning for the Nearby-cameras list. Omit to derive from the source
   *  hazard (fire/hotspot → smoke-priority + line-of-sight; flood / ice / CAP weather →
   *  all nearby live cameras in range). Pass explicitly to override. */
  cameraTuning?: 'fire' | 'all-nearby';
}): JSX.Element {
  // Bumped after Recommend Dispatch raises a resource request, to refresh the list.
  const [rrReload, setRrReload] = useState(0);
  // Coordination re-arch Phase 2 — the linked-community count gates the Evacuation
  // entry (§4.30, data-driven: any incident with ≥1 linked community). Sourced from
  // the SAME incident-detail load IncidentManagementSection already performs (via its
  // onDetailLoaded callback below) — no duplicate /api/incidents/:id round-trip. null
  // while unowned / before the detail loads (entry hidden).
  const [linkedCommunityCount, setLinkedCommunityCount] = useState<number | null>(null);
  // #445-1 — the owned incident's doctrine-safe number, for the air-support request surface.
  const [incidentNumberFromDetail, setIncidentNumberFromDetail] = useState<string | null>(null);
  // Reset when the owned incident changes so a stale count never gates the entry for
  // a different incident; the management section's onDetailLoaded refills it.
  useEffect(() => {
    setLinkedCommunityCount(null);
    setIncidentNumberFromDetail(null);
  }, [incidentId]);
  const handleIncidentDetailLoaded = useCallback((d: IncidentDetail) => {
    setLinkedCommunityCount(d.linked_community_count ?? 0);
    setIncidentNumberFromDetail(d.incident_number ?? null);
  }, []);

  const dispatchType = source ? (SOURCE_TO_INCIDENT_TYPE[source.recordType] ?? null) : null;
  const alertKind = source ? (SOURCE_TO_ALERT_KIND[source.recordType] ?? null) : null;
  const isHotspotSource = source?.recordType === 'high_risk_hotspot';
  // #443 — explicit prop wins; otherwise derive from the source hazard so a non-fire
  // surface (incl. the Incidents-tab work surface bound to a flood/ice/CAP incident)
  // shows all nearby cameras, while fire/hotspot keep the smoke-priority + LOS subset.
  const effectiveCameraTuning: 'fire' | 'all-nearby' =
    cameraTuning ??
    (source && NON_FIRE_CAMERA_SOURCE_TYPES.has(source.recordType) ? 'all-nearby' : 'fire');
  // Resource-thread anchor point — the source centroid when present, else the
  // incident centroid passed in (staging "declare new area at this point").
  const threadLat = source?.lat ?? lat ?? 0;
  const threadLon = source?.lon ?? lon ?? 0;
  // The resource thread (drones + requests + evac) is incident-scoped, so it shows
  // whenever the alert is OWNED — source-bound or manually declared. A hotspot's
  // requests are hotspot-keyed (visible pre-ownership too), so render the requests
  // section whenever either key resolves.
  const showResourceRequests = isHotspotSource || incidentId != null;

  return (
    <div className="space-y-4" data-testid="incident-work-surface">
      {/* Tracked-incident block: the management section once owned; else (alert
          side, source present) the one-click Declare affordance. A manually-
          declared incident always carries incidentId ⇒ goes straight to management. */}
      {incidentId ? (
        <IncidentManagementSection
          incidentId={incidentId}
          onOpenSitrep={onOpenSitrep}
          onOpenReplay={onOpenReplay}
          replayActive={replayActive}
          onDetailLoaded={handleIncidentDetailLoaded}
        />
      ) : source ? (
        <DeclareIncidentButton
          name={source.declareName}
          hazardType={source.declareHazardType ?? 'wildfire'}
          sourceRecordType={source.recordType}
          sourceRecordId={source.recordId}
          lon={source.lon}
          lat={source.lat}
          onDeclared={(inc) => onIncidentDeclared?.(inc)}
        />
      ) : null}

      {/* Alert-derived action set — ONLY when a source record is present (a manual
          incident has nothing to confirm / dispatch / stage against). Bound to the
          SOURCE id + centroid, identical to the alert detail. */}
      {source && (
        <>
          {/* Nearby cameras — hazard-tuned (#443): smoke-priority + line-of-sight for a
              fire/hotspot (confirm visually before dispatch); all nearby live cameras in
              range for a flood / ice / CAP weather area. */}
          <NearbyCamerasSection
            lon={source.lon}
            lat={source.lat}
            onSelectCamera={onSelectCamera}
            cameraTuning={effectiveCameraTuning}
          />

          {/* #185 — cross-link to any Assistance Request raised against this alert
              (contextual: renders nothing when none is linked / no cross-linkable kind). */}
          {onOpenConnectedAssist && alertKind && (
            <ConnectedAssistLink
              alertKind={alertKind}
              recordId={source.recordId}
              onOpen={onOpenConnectedAssist}
            />
          )}

          {/* R.3.2 — nearest response resources for this incident. */}
          <NearbyResponseAssets
            lat={source.lat}
            lon={source.lon}
            incidentType={dispatchType ?? 'cap_alert'}
            incidentId={source.recordId}
            onSelectAsset={onSelectAsset}
          />

          {/* R.3.3 — live ADS-B aircraft in range (water bombers highlighted). */}
          <LiveAircraftSection lat={source.lat} lon={source.lon} onSelectAircraft={onSelectAircraft} />

          {/* #445-1 — Air-support REQUEST to the MNRF/AFFES fire centre (records-not-confers).
              WILDFIRE surface only (dispatchType ⟺ fire/hotspot source). A SIBLING to the
              read-only ADS-B section above — the seat REQUESTS, it never tasks the aircraft. */}
          {dispatchType && (
            <RequestAirSupport
              incidentId={incidentId ?? undefined}
              incidentNumber={incidentNumberFromDetail}
              hazardType={source.declareHazardType ?? 'wildfire'}
            />
          )}

          {/* #348b — drone marketplace front door, anchored on the source; tasks the
              drone to THIS incident once owned. */}
          <IncidentRequestLiveEyes
            lat={source.lat}
            lon={source.lon}
            aoLabel={source.aoLabel}
            incidentId={incidentId}
          />

          {/* R.3.6 — Confirm Fire / Recommend Dispatch (dispatchable sources only). */}
          {dispatchType && (
            <DispatchActions
              incidentType={dispatchType}
              incidentId={source.recordId}
              lat={source.lat}
              lon={source.lon}
              // #335 — hazard-aware confirm label/toast; a non-fire hazard never inherits
              // a fire word (the backend recommendation_type stays 'confirm_fire').
              hazardNoun={confirmHazardNoun(source.declareHazardType)}
              onResourceRequestCreated={(created) => {
                // Task #92 — adopt the auto-promoted incident (active_fire) so the
                // management section + resource thread appear, then refresh the list.
                if (created.promoted_incident_id) onIncidentPromoted?.(created.promoted_incident_id);
                setRrReload((t) => t + 1);
              }}
            />
          )}
        </>
      )}

      {/* Coordination re-arch Phase 2 — the incident RESOURCE THREAD (IMS Ruling 3 +
          Drone Ruling 3): every resource tasked to THIS incident, worked from one
          place. Drones (drone_missions, #345) + the 7-state resource_requests, plus
          the Evacuation entry when communities are linked. Incident-scoped, so it
          shows for a source-bound OR a manually-declared owned incident. */}
      {incidentId && (
        <IncidentDronesPanel
          incidentId={incidentId}
          // A source-bound incident already has the standalone Request-live-eyes
          // affordance above, so the panel stays read-only there. A source-LESS
          // (manually-declared) incident has none — give it the inline "Request
          // another drone" tasking affordance anchored on the incident centroid, so
          // a drone CAN be tasked FROM any owned incident (Drone Ruling 3), not just
          // alert-sourced ones.
          requestAo={!source && lat != null && lon != null ? { lat, lon } : null}
          // Phase 4e — a live-feed row clicks through to its mission/feed (§I.2).
          onSelectMission={onSelectMission}
        />
      )}
      {showResourceRequests && (
        <ResourceRequestsSection
          hotspotId={isHotspotSource && source ? source.recordId : undefined}
          incidentId={isHotspotSource ? undefined : (incidentId ?? undefined)}
          reloadToken={rrReload}
          lat={threadLat}
          lon={threadLon}
        />
      )}
      {incidentId && onOpenEvacuation && hasEvacuationEvidence(linkedCommunityCount) && (
        <EvacuationEntryButton onClick={() => onOpenEvacuation(incidentId)} />
      )}

      {/* #328p2 — the incident-scoped inter-ops information-sharing thread (R2a): two-way,
          multi-destination messages + RFAs shared with other EOCs, ISC, and federal partners,
          each with its per-recipient received/acknowledged/sent breakdown. */}
      {incidentId && <InteropsThreadPanel contextType="incident" contextId={incidentId} />}
    </div>
  );
}

/** Source record type → an operator-facing label for the incident-detail "Source"
 *  context line (the thread, forward direction — what the incident was promoted from). */
const INCIDENT_SOURCE_LABEL: Record<IncidentSourceRecordType, string> = {
  high_risk_hotspot: 'AI-fused high-risk hotspot',
  active_wildland_fire: 'Reported active wildland fire',
  cap_alert: 'Public-safety (CAP) alert',
  // #425 — declare-from-any-hazard sources.
  flood_anomaly: 'Flood gauge anomaly',
  ice_storm_anomaly: 'Ice-storm outage-cluster anomaly',
  // #462 — an incident originated from an operator evacuation order (the accounting bridge,
  // when no proximate hazard incident existed to attach the order to).
  operator_evac_order: 'Evacuation order',
};

/** The incident detail's source-alert context (Coordination re-arch Phase 1) — a
 *  light readout of what this tracked incident was promoted from. */
function IncidentSourceContext({
  sourceRecordType,
}: {
  sourceRecordType: IncidentSourceRecordType | null;
}): JSX.Element {
  const label = sourceRecordType
    ? INCIDENT_SOURCE_LABEL[sourceRecordType]
    : 'Manually owned (no source alert)';
  return (
    <div
      data-testid="incident-source-context"
      className="rounded border border-slate-700/70 bg-slate-800/30 px-2 py-1.5"
    >
      <p className="text-[10px] uppercase tracking-wider text-slate-500">Source</p>
      <p className="text-[12px] text-slate-200">{label}</p>
    </div>
  );
}

/**
 * Side-panel body for an AI-fused high-risk hotspot (E.2.3) — the flagship
 * detect → confirm workflow. Shows the IMS Response Level, the primary detection,
 * the confidence, a corroborating-signals breakdown (fetched from the detail
 * endpoint for the full payload), nearby smoke-priority cameras for visual
 * confirmation, and the (disabled) R.3 Confirm-Fire / Recommend-Dispatch hooks.
 */
export function HighRiskHotspotBody({
  hotspot,
  onSelectCamera,
  onSelectAsset,
  onSelectAircraft,
  onOpenSitrep,
  onOpenConnectedAssist,
  onOpenEvacuation,
  onFlythrough,
  flythroughActive = false,
  onOpenReplay,
  aarReplayActive = false,
  onFlyToIncident,
  onSelectMission,
}: {
  hotspot: HighRiskHotspotFeature;
  onSelectCamera?: (c: CameraFeature) => void;
  onSelectAsset?: (a: ResponseAssetFeature) => void;
  onSelectAircraft?: (a: AircraftFeature) => void;
  onOpenSitrep?: (id: string) => void;
  onOpenConnectedAssist?: (requestId: string) => void;
  /** §4.30 — open the incident-scoped Evacuation Logistics panel (Phase 2; the
   *  work-surface gates it on ≥1 linked community, so it's hidden for a typical
   *  hotspot with none). */
  onOpenEvacuation?: (incidentId: string) => void;
  /** #179/#235 — start/stop the operator 3D flythrough around this hotspot. */
  onFlythrough?: (target: { lon: number; lat: number; label?: string }) => void;
  flythroughActive?: boolean;
  /** #180 — enter the AAR time replay for this hotspot's tracked incident. */
  onOpenReplay?: (incidentId: string) => void;
  aarReplayActive?: boolean;
  /** #356 — re-centre the COP on this hotspot. */
  onFlyToIncident?: (lat: number, lon: number) => void;
  /** Phase 4e — feed click-through on the incident's drone list (§I.2). */
  onSelectMission?: (m: DroneMissionView) => void;
}): JSX.Element {
  const p = hotspot.properties;
  const [lon, lat] = hotspot.geometry.coordinates;
  const [detail, setDetail] = useState<HighRiskHotspotDetail | null>(null);
  // SITL surface — the tracked incident this hotspot was promoted to (if any);
  // the management section renders only when one resolves.
  const [incidentId, setIncidentId] = useState<string | null>(null);

  // IMS audit: log when the operator opens a high-risk hotspot's detail panel.
  useEffect(() => {
    logAction('view_high_risk_hotspot_detail', {
      hotspot_id: p.id,
      response_level: p.response_level,
      confidence: p.confidence,
    });
  }, [p.id, p.response_level, p.confidence]);

  // The list feature carries only a signal SUMMARY; fetch the detail for the
  // full corroborating_signals payload (station lists, fire id, score breakdown).
  useEffect(() => {
    const ctl = new AbortController();
    void fetchHighRiskHotspotDetail(p.id, ctl.signal)
      .then(setDetail)
      .catch(() => undefined);
    return () => ctl.abort();
  }, [p.id]);

  // Resolve any incident this hotspot was promoted to (idempotent lookup), so the
  // SITL incident-management section shows when the hotspot is a tracked incident.
  useEffect(() => {
    const ctl = new AbortController();
    setIncidentId(null);
    void fetchIncidentForSource('high_risk_hotspot', p.id, ctl.signal)
      .then((inc) => setIncidentId(inc?.id ?? null))
      .catch(() => undefined);
    return () => ctl.abort();
  }, [p.id]);

  const sig = detail?.properties.corroborating_signals ?? null;
  const summary = p.corroborating_signals;
  const primary = p.primary_hotspot;

  const aqhi = sig?.aqhi_nearby ?? [];
  const purpleair = sig?.purpleair_spike ?? [];
  const aqhiMax = aqhi.length ? Math.max(...aqhi.map((a) => a.aqhi_value)) : null;
  const pmMax = purpleair.length ? Math.max(...purpleair.map((s) => s.pm2_5)) : null;
  const aqhiCount = sig ? aqhi.length : summary.aqhi_nearby_count;
  const pmCount = sig ? purpleair.length : summary.purpleair_spike_count;
  const fwi = sig ? sig.fwi : summary.fwi;
  const activeFireId = sig?.active_fire_nearby_id ?? null;
  const activeFireNearby = sig ? activeFireId !== null : summary.active_fire_nearby;

  return (
    <div className="px-4 py-4 space-y-4" data-testid="high-risk-hotspot-body">
      <div>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">
          AI-Fused High-Risk Hotspot
        </p>
        <h2 className="text-base font-semibold">High-risk hotspot</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          {lat.toFixed(4)}°N, {lon.toFixed(4)}°E
        </p>
        <div className="mt-2">
          {/* #613 — a raw AI-fused high-risk hotspot (un-owned) → the machine Threat axis,
              NEVER the §5 "Response Level" (that is an owned incident's IC-assessed scale). */}
          <ResponseLevelBadge level={p.response_level} axis="threat" />
        </div>
        {/* #391 — inline ✓ Ack for an urgent (RL≥3) fused hotspot, reusing the queue's
            unified-workstate ack path (map dot + queue row stay in sync). Self-hides
            for demoted L1/L2 candidates. */}
        <div className="mt-3">
          <AnomalyAckButton item={hotspotToQueueItem(hotspot)} />
        </div>
        {/* #356 — jump the COP to this hotspot (it doesn't auto-fly on select). */}
        <div className="mt-3">
          <FlyToIncidentButton lat={lat} lon={lon} onFlyToIncident={onFlyToIncident} />
        </div>
        {/* Coordination re-arch Phase 1 — the LIGHT owned-incident read (the alert↔
            incident thread, reverse direction). Driven by the API join on the feature
            (high_risk_hotspots LEFT JOIN incidents ON source_record_id); the full
            OC-fire glyph/owned-map-treatment is Phase 5. */}
        {p.owned_incident && (
          <div className="mt-2">
            <span
              data-testid="hotspot-owned-incident"
              className="inline-flex items-center gap-1 rounded bg-indigo-900/40 px-1.5 py-0.5 text-[10px] font-medium text-indigo-300"
              title={`Owned — tracked as incident ${p.owned_incident.incident_number} (${p.owned_incident.status})`}
            >
              <span aria-hidden>🔗</span> Owned · incident {p.owned_incident.incident_number}
            </span>
          </div>
        )}
        {/* Ownership-coordination — the cross-agency "who holds command + how to reach them" read. */}
        {p.owned_incident && <OwnershipCoordinationBlock owned={p.owned_incident} />}
      </div>

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">Primary detection</h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">Source</dt>
          <dd className="font-mono">{primary.source}</dd>
          {/* #370 — this FRP is the cluster MAX (carried onto the primary so a
              freshest-wins GOES head, which reports no FRP, doesn't hide the
              fire's real intensity). It can come from a corroborating platform,
              NOT the Source listed above — so the label says "Peak" and the
              tooltip is explicit, rather than implying it is this source's reading. */}
          <dt className="text-slate-400">Peak FRP</dt>
          <dd
            className="font-mono"
            title="Strongest fire radiative power across the fused cluster's detections — may come from a corroborating platform, not the primary Source listed above."
          >
            {primary.frp != null ? `${primary.frp.toLocaleString(undefined, { maximumFractionDigits: 1 })} MW` : '—'}
          </dd>
          <dt className="text-slate-400">Observed</dt>
          <dd className="text-xs" title={formatObserved(p.observed_at)}>
            {relativeTime(p.observed_at)}
          </dd>
          <dt className="text-slate-400">Confidence</dt>
          <dd className="font-mono" data-testid="hotspot-confidence">
            {p.confidence} <span className="text-slate-500">of 100</span>
          </dd>
        </dl>
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
          Corroborating signals
        </h3>
        <div className="space-y-1" data-testid="corroborating-signals">
          <SignalRow
            label="Satellite corroboration"
            on={sig ? sig.satellite_corroboration : summary.satellite_corroboration}
            detail={(sig ? sig.satellite_corroboration : summary.satellite_corroboration) ? 'second platform' : undefined}
          />
          <SignalRow
            label="Fire Weather Index"
            on={fwi != null && fwi > 10}
            detail={fwi != null ? `FWI ${fwi.toFixed(1)}` : 'n/a'}
          />
          <SignalRow
            label="AQHI elevated nearby"
            on={aqhiCount > 0}
            detail={
              aqhiCount > 0
                ? `${aqhiCount} station${aqhiCount === 1 ? '' : 's'}${aqhiMax != null ? ` · max ${aqhiMax}` : ''}`
                : undefined
            }
          />
          <SignalRow
            label="PurpleAir PM2.5 spike"
            on={pmCount > 0}
            detail={
              pmCount > 0
                ? `${pmCount} sensor${pmCount === 1 ? '' : 's'}${pmMax != null ? ` · max ${pmMax.toFixed(0)} µg/m³` : ''}`
                : undefined
            }
          />
          <SignalRow
            label="HMS smoke plume overlap"
            on={sig ? sig.hms_overlap : summary.hms_overlap}
          />
          <SignalRow
            label="Active fire nearby"
            on={activeFireNearby}
            detail={activeFireId ? `fire ${activeFireId.slice(0, 8)}` : undefined}
          />
          <SignalRow
            label="New-area boost (possible undetected fire)"
            on={sig ? sig.new_area_boost : summary.new_area_boost}
          />
        </div>
      </section>

      {/* #442 Slice 2 + #447 Slice 3b — the surfacing-demotion verdict. Surfaced
          distinctly (not a green corroboration row) so the Situation Unit can DEFEND the
          demotion in a briefing. Gate WIDENED (R3) from static_masked to ANY demoting
          verdict, so the directional connected-fire inversion ("this hotspot IS the known
          fire's spread") renders too — not only the static-industrial-heat mask. */}
      {(sig?.genuineness?.demoted || sig?.genuineness?.static_masked) && (
        <section data-testid="genuineness-verdict">
          <div
            className={`rounded border px-3 py-2 text-xs ${
              sig.genuineness.demoted
                ? 'border-amber-700/60 bg-amber-950/30 text-amber-200'
                : 'border-rose-700/60 bg-rose-950/30 text-rose-200'
            }`}
          >
            <div className="font-medium" data-testid="genuineness-verdict-headline">
              {sig.genuineness.demoted_reason === 'connected_fire'
                ? `Demoted — known fire${
                    sig.genuineness.connected_to_fire?.fire_name
                      ? ` ${sig.genuineness.connected_to_fire.fire_name}`
                      : ''
                  } (this hotspot is its spread, not a new fire)`
                : sig.genuineness.demoted
                  ? 'Demoted — static industrial heat source (not a fire)'
                  : 'Static heat source — FRP surge kept it urgent (possible fire on site)'}
            </div>
            {sig.genuineness.demoted_reason === 'connected_fire' ? (
              <div className="mt-0.5 text-amber-300/80">
                The reported fire raises the alert; a second urgent for the same fire is
                suppressed.
                {sig.genuineness.persistence_looks != null &&
                  ` · ${sig.genuineness.persistence_looks} look${
                    sig.genuineness.persistence_looks === 1 ? '' : 's'
                  }`}
              </div>
            ) : (
              sig.genuineness.static_mask_source && (
                <div className="mt-0.5 text-amber-300/80">
                  {sig.genuineness.static_mask_source.name} ·{' '}
                  {sig.genuineness.static_mask_source.source_type.replace(/_/g, ' ')}
                  {sig.genuineness.baseline_frp != null &&
                    ` · ambient baseline ${sig.genuineness.baseline_frp.toFixed(0)} MW`}
                </div>
              )
            )}
          </div>
        </section>
      )}

      {/* #448 Track A Slice 3c — the values-at-risk VETO callout. The INVERSE of a demotion:
          a genuine, unconnected hotspot KEPT on the urgent surface because a NATIONAL
          populated place is within range (the Municipal NO-GO closer). Emerald, so the
          Situation Unit reads "near a community → stays red" and can defend WHY this low-FRP /
          low-confidence hotspot is urgent. Names the place + distance (mirrors the OC-fire
          threatened-community line) + flags the downwind evacuation-risk lift. */}
      {sig?.genuineness?.values_kept_urgent && sig.genuineness.nearest_place && (
        <section data-testid="values-veto-verdict">
          <div className="rounded border px-3 py-2 text-xs border-emerald-700/60 bg-emerald-950/30 text-emerald-200">
            <div className="font-medium" data-testid="values-veto-verdict-headline">
              Kept urgent —{' '}
              {sig.genuineness.nearest_place.distance_m < 100
                ? `at ${sig.genuineness.nearest_place.name}`
                : `${(sig.genuineness.nearest_place.distance_m / 1000).toFixed(1)} km from ${sig.genuineness.nearest_place.name}`}{' '}
              (genuine fire near a community)
            </div>
            {/* #662 — the drone-tasking RECOMMENDATION is STRUCK. This is the K1 finding of the §G
                design gate (docs/fire-scorer-rebuild-g-gate-2026-07-13.md §1.1): #635 struck the
                identical drone-tasking recommendation from the OC-FIRE panel, but that strike never
                reached this HOTSPOT surface. Ontario IMS App A #70 seats a recommendation for
                protective action in the Scientific/Technical section, which "is stood up in an
                incident" and "reports directly to the incident commander" — PRE-INCIDENT there is no
                IC, so a recommendation has no doctrinal seat. The COP states an OBSERVED FACT only
                (mirrors #647): the register is "in wind path (±45°)", and the bare word "downwind"
                never reaches an operator string (§4.2 keeps it a backend field).
                ★ Fact-only, NOT the full #647 line: the hotspot payload carries ONLY a 2-state
                `nearest_place.downwind` — fusion.ts (:1030) projects the shared 3-state cone
                (resolveDownwindState) `=== true` BEFORE it reaches the payload, so `false` CONFLATES
                known-out-of-cone with UNKNOWN/stale/calm wind — AND it carries NO wind observation
                (speed/direction/observed_at) like the OC-fire `threatened_community.wind` does. So a
                positive in-path FACT is asserted ONLY on `=== true` (a genuine in-cone / known-fresh-
                wind fact); any other state makes NO in-path / not-in-path claim (an UNKNOWN is never
                "not downwind" — #647 PIN 1/3). Full #647 parity (3-state + observed wind + age) needs
                a payload change — DEFERRED, out of this wave's scope (see the #662 gap row). */}
            <div className="mt-0.5 text-emerald-300/80" data-testid="values-veto-wind-path">
              {sig.genuineness.nearest_place.downwind
                ? `${sig.genuineness.nearest_place.name} is in the wind path (±45°).`
                : 'Wind path not established.'}
            </div>
          </div>
        </section>
      )}

      {/* #179/#235 — operator 3D flythrough around the hotspot site (on-demand
          photoreal tiles; prior view restored when the tour ends). Same pattern
          as FireBody / CommunityBody. */}
      {onFlythrough && (
        <FlythroughButton
          target={{
            lon,
            lat,
            label: `High-risk hotspot — ${lat.toFixed(3)}°N ${Math.abs(lon).toFixed(3)}°W`,
          }}
          active={flythroughActive}
          onFlythrough={onFlythrough}
        />
      )}

      {/* IMS Ruling 1 — the full incident work-surface, bound to this hotspot. The
          SAME component renders on the Incidents-tab incident detail, so the operator
          works one owned object from either place (Declare → confirm → recommend
          dispatch → request a drone → stage resources → advance + close). */}
      <IncidentWorkSurface
        source={{
          recordType: 'high_risk_hotspot',
          recordId: p.id,
          lat,
          lon,
          aoLabel: 'this hotspot',
          declareName: `High-risk hotspot — ${lat.toFixed(3)}°N ${Math.abs(lon).toFixed(3)}°W`,
          declareHazardType: 'wildfire',
        }}
        incidentId={incidentId}
        lat={lat}
        lon={lon}
        onIncidentDeclared={(inc) => setIncidentId(inc.id)}
        onSelectCamera={onSelectCamera}
        onSelectAsset={onSelectAsset}
        onSelectAircraft={onSelectAircraft}
        onOpenSitrep={onOpenSitrep}
        onOpenConnectedAssist={onOpenConnectedAssist}
        onOpenEvacuation={onOpenEvacuation}
        onOpenReplay={onOpenReplay}
        onSelectMission={onSelectMission}
        replayActive={aarReplayActive}
      />

      <p className="text-[10px] text-slate-500 leading-relaxed">
        Fused by the F.1 engine from satellite + weather + air-quality + smoke
        signals. Threat Level is the machine-derived hazard severity (§4.3 handle) — a
        raw hotspot carries no IC-assessed §5 Response Level. Confirm visually from a
        nearby camera before any dispatch action.
      </p>
    </div>
  );
}

/** Severity/urgency/certainty pill. Extreme severity reads hottest (red). */
function CapBadge({ label, value }: { label: string; value: string | null }): JSX.Element | null {
  if (!value) return null;
  const tone: Record<string, string> = {
    Extreme: 'bg-rose-500/20 text-rose-300',
    Severe: 'bg-amber-500/20 text-amber-300',
    Immediate: 'bg-rose-500/20 text-rose-300',
    Observed: 'bg-sky-500/20 text-sky-300',
  };
  const cls = tone[value] ?? 'bg-slate-700 text-slate-300';
  return (
    <span
      className={`inline-block text-[10px] px-1.5 py-0.5 rounded ${cls}`}
      title={`${label}: ${value}`}
    >
      {value}
    </span>
  );
}

/**
 * Extreme Weather Warning side panel (Phase EWW / L.0 Layer #2 blending). Opened
 * by clicking a CAP polygon on the blended Urgent Alerts surface. Surfaces the
 * full warning — event, severity/urgency/certainty, affected region, issued/
 * expires times, sender, and the headline/description/instruction text.
 *
 * The list feature carries the core CAP properties; the detail fetch adds the
 * long-form headline/description + sender. Sectoring & dispatch is an R.3
 * placeholder (CAP dispatch isn't wired yet — flagged below).
 */
export function ExtremeWeatherBody({
  alert,
  onOpenConnectedAssist,
  onSelectCamera,
  onSelectAsset,
  onSelectAircraft,
  onFlyToIncident,
  onOpenSitrep,
  onOpenEvacuation,
  onOpenReplay,
  onSelectMission,
  aarReplayActive = false,
}: {
  alert: CapAlertFeature;
  onOpenConnectedAssist?: (requestId: string) => void;
  /** #357 — open a nearby camera covering the warning area. */
  onSelectCamera?: (c: CameraFeature) => void;
  /** #357 — open a nearby response resource. */
  onSelectAsset?: (a: ResponseAssetFeature) => void;
  /** #357 — open a nearby live-aircraft track. */
  onSelectAircraft?: (a: AircraftFeature) => void;
  /** #356 — re-centre the COP on this warning. */
  onFlyToIncident?: (lat: number, lon: number) => void;
  /** #425 — IncidentWorkSurface handlers for the un-gated CAP declare path. */
  onOpenSitrep?: (id: string) => void;
  onOpenEvacuation?: (incidentId: string) => void;
  onOpenReplay?: (incidentId: string) => void;
  onSelectMission?: (m: DroneMissionView) => void;
  aarReplayActive?: boolean;
}): JSX.Element {
  const p = alert.properties;
  const [detail, setDetail] = useState<CapAlertDetail | null>(null);
  // #425 — the incident promoted FROM this CAP alert (source_record link), if any.
  const [incidentId, setIncidentId] = useState<string | null>(null);
  // CAP geometry is a MultiPolygon; reuse the queue converter's anchor (first ring
  // vertex) so fly-to / nearby / live-eyes share the exact point Send-to-wall uses.
  // The same queue item also drives the #391 inline ✓ Ack (Severe/Extreme = urgent).
  const queueItem = capToQueueItem(alert);
  const { lat, lon } = queueItem;

  // #425 — resolve the incident promoted from THIS CAP alert (idempotent on the source).
  useEffect(() => {
    setIncidentId(null);
    const ctl = new AbortController();
    void fetchIncidentForSource('cap_alert', p.id, ctl.signal)
      .then((inc) => setIncidentId(inc?.id ?? null))
      .catch(() => setIncidentId(null));
    return () => ctl.abort();
  }, [p.id]);

  // IMS audit: log when the operator opens a CAP alert detail panel.
  useEffect(() => {
    logAction('view_extreme_weather_detail', {
      cap_id: p.id,
      event: p.event,
      severity: p.severity,
    });
  }, [p.id, p.event, p.severity]);

  useEffect(() => {
    const ctl = new AbortController();
    void fetchCapAlertDetail(p.id, ctl.signal)
      .then(setDetail)
      .catch(() => undefined);
    return () => ctl.abort();
  }, [p.id]);

  const dp = detail?.properties;
  const headline = dp?.headline ?? null;
  const description = dp?.description ?? null;
  const sender = dp?.sender_name ?? dp?.sender ?? null;

  return (
    <div className="px-4 py-4 space-y-4" data-testid="extreme-weather-body">
      <div>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">
          Public Alert · CAP
        </p>
        <h2 className="text-base font-semibold">{p.event ?? 'Weather warning'}</h2>
        {/* #159 — visible NPAS / Alert Ready provenance on every CAP-sourced row. */}
        <span
          data-testid="cap-npas-provenance"
          title="Issued through Canada's National Public Alerting System (Alert Ready), broadcast via NAAD"
          className="mt-2 inline-flex items-center gap-1 rounded bg-fuchsia-500/15 px-1.5 py-0.5 text-[10px] font-medium text-fuchsia-300"
        >
          📢 Alert Ready · NPAS
        </span>
        <div className="mt-2 flex flex-wrap gap-1">
          <CapBadge label="Severity" value={p.severity} />
          <CapBadge label="Urgency" value={p.urgency} />
          <CapBadge label="Certainty" value={p.certainty} />
        </div>
        {/* #391 — inline ✓ Ack for an urgent (Severe/Extreme) CAP, reusing the queue's
            unified-workstate ack path. Self-hides for sub-Severe alerts. */}
        <div className="mt-3">
          <AnomalyAckButton item={queueItem} />
        </div>
        {/* #356 — jump the COP to the warning area (CAP selections don't auto-fly). */}
        <div className="mt-3">
          <FlyToIncidentButton lat={lat} lon={lon} onFlyToIncident={onFlyToIncident} label="Fly to warning area" />
        </div>
      </div>

      {/* R2/#478 — an evacuation / life-safety CAP is a PROTECTIVE ACTION: link it to
          the incident it protects (auto-links by proximity; always unlinkable). Routine
          weather CAPs are NOT in scope and skip this control. */}
      {classifyCapLifeSafety({ event: p.event, responseLevel: p.response_level }) !== 'routine' && (
        <LinkToIncidentControl
          kind="cap_alert"
          alertId={p.id}
          incidentId={p.incident_id ?? null}
          linkMode={p.incident_link_mode ?? null}
        />
      )}

      {p.area_desc && (
        <section>
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1">Affected area</h3>
          <p className="text-sm text-slate-200" data-testid="cap-area-desc">
            {p.area_desc}
          </p>
        </section>
      )}

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">Timing</h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">Issued</dt>
          <dd className="text-xs" title={formatObserved(p.sent)}>
            {relativeTime(p.sent)}
          </dd>
          {p.expires_at && (
            <>
              <dt className="text-slate-400">Expires</dt>
              <dd className="text-xs" title={formatObserved(p.expires_at)}>
                {relativeTime(p.expires_at)}
              </dd>
            </>
          )}
          {sender && (
            <>
              <dt className="text-slate-400">Sender</dt>
              <dd className="text-xs font-mono break-all">{sender}</dd>
            </>
          )}
        </dl>
      </section>

      {(headline || description) && (
        <section>
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1">Warning</h3>
          {headline && <p className="text-sm font-medium text-slate-100">{headline}</p>}
          {description && (
            <p className="text-sm text-slate-300 mt-1 whitespace-pre-line">{description}</p>
          )}
        </section>
      )}

      {p.instruction && (
        <section>
          <h3 className="text-xs uppercase tracking-wider text-amber-300/90 mb-1">
            Recommended action
          </h3>
          <p className="text-sm text-slate-200 whitespace-pre-line" data-testid="cap-instruction">
            {p.instruction}
          </p>
        </section>
      )}

      {/* #425 — the #185 connected-assist cross-link + the #357 nearby cameras /
          response resources / live aircraft are now provided by the IncidentWorkSurface
          source-action set below (consolidated onto the same surface the fire body uses,
          no longer a hand-rolled twin). #443 — the work surface's nearby-cameras are now
          hazard-tuned: CAP passes cameraTuning='all-nearby' (every nearby live camera in
          range, not the fire smoke-priority + line-of-sight subset). */}

      {/* #425 — declare-from-any-hazard: the SAME IncidentWorkSurface the fire body
          mounts, anchored on this CAP warning (replaces the R.3 "coming soon"
          placeholder). Unowned ⇒ one-click Declare; once owned it opens to the
          incident management section. SOURCE_TO_INCIDENT_TYPE leaves cap_alert unmapped
          ⇒ the fire-style Confirm / Recommend-Dispatch correctly stays hidden. */}
      <section data-testid="cap-incident-surface">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">Incident</h3>
        <IncidentWorkSurface
          source={{
            recordType: 'cap_alert',
            recordId: p.id,
            lat,
            lon,
            aoLabel: 'this warning area',
            declareName: headline ?? p.event ?? 'Weather warning',
            declareHazardType: 'cap_warning',
          }}
          incidentId={incidentId}
          lat={lat}
          lon={lon}
          cameraTuning="all-nearby"
          onIncidentDeclared={(inc) => setIncidentId(inc.id)}
          onSelectCamera={onSelectCamera}
          onSelectAsset={onSelectAsset}
          onSelectAircraft={onSelectAircraft}
          onOpenSitrep={onOpenSitrep}
          onOpenConnectedAssist={onOpenConnectedAssist}
          onOpenEvacuation={onOpenEvacuation}
          onOpenReplay={onOpenReplay}
          onSelectMission={onSelectMission}
          replayActive={aarReplayActive}
        />
      </section>

      <p className="text-[10px] text-slate-500 leading-relaxed">
        Source: Canada&apos;s National Public Alerting System (NPAS / Alert Ready),
        broadcast via NAAD using the Common Alerting Protocol. Rendered as part of
        the multi-source Urgent Alerts layer.
      </p>
    </div>
  );
}

/**
 * #195 — co-located camera picker. When the operator clicks a cluster that zoom
 * can't separate (the 3 Parry Sound angles on one dock, the ~7 Canmore townsite
 * cams), the map drops the cluster's members here as a selectable list. Picking
 * one opens it as a normal camera selection (focus + fly + feed), tying into the
 * persistent selection rail.
 */
function CameraListBody({
  cameras,
  onSelectCamera,
}: {
  cameras: CameraFeature[];
  onSelectCamera?: (c: CameraFeature) => void;
}): JSX.Element {
  return (
    <div className="px-4 py-4 space-y-3" data-testid="camera-list-body">
      <div>
        <h2 className="text-base font-semibold">{cameras.length} cameras here</h2>
        <p className="text-xs text-slate-400 mt-0.5">
          These cameras share one site — too close to separate on the map. Pick one to
          open its feed.
        </p>
      </div>
      <ul className="space-y-1.5" data-testid="camera-list">
        {cameras.map((c) => {
          const live = c.properties.status === 'live' && c.properties.stream_type !== 'snapshot';
          return (
            <li key={c.id}>
              <button
                type="button"
                data-testid="camera-list-item"
                onClick={() => onSelectCamera?.(c)}
                disabled={!onSelectCamera}
                className="flex w-full items-center gap-2 rounded border border-slate-700 bg-slate-800 px-3 py-2 text-left text-sm hover:bg-slate-700 disabled:cursor-default"
              >
                <span
                  aria-hidden
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: live ? COVERAGE_LIVE : COVERAGE_SNAPSHOT }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{c.properties.name}</span>
                  <span className="block truncate text-[11px] text-slate-400">
                    {c.properties.source} · {c.properties.stream_type}
                  </span>
                </span>
                <StatusTag camera={c} />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * #415 (Phase 6b) — one row's display summary for the co-located incident list.
 * Family label + a short context line + the machine-derived Threat Level (when the
 * family carries one — smoke + outages are not level-scored; #613). Pure, no fetch.
 */
function incidentRowSummary(ref: ColocatedIncidentRef): {
  label: string;
  sub: string;
  rl: number | null;
} {
  switch (ref.kind) {
    case 'highRiskHotspot':
      return {
        label: 'High-risk hotspot',
        sub: `${ref.feature.properties.confidence}% confidence`,
        rl: ref.feature.properties.response_level,
      };
    case 'capAlert':
      return {
        label: ref.feature.properties.event ?? 'Extreme weather warning',
        sub: ref.feature.properties.area_desc ?? '',
        rl: ref.feature.properties.response_level,
      };
    case 'smokeDetectionAnomaly':
      return { label: 'Smoke detection', sub: ref.feature.properties.camera_name, rl: null };
    case 'iceStormAnomaly':
      return {
        label: 'Ice-storm anomaly',
        sub: ref.feature.properties.utilities.join(', '),
        rl: ref.feature.properties.response_level,
      };
    case 'floodAnomaly':
      return {
        label: 'Flood anomaly',
        sub:
          ref.feature.properties.station_name ??
          ref.feature.properties.river ??
          ref.feature.properties.station_number,
        rl: ref.feature.properties.response_level,
      };
    case 'utilityOutage':
      return { label: 'Power outage', sub: ref.feature.properties.utility, rl: null };
  }
}

/**
 * #415 (Phase 6b) — the co-located incident disambiguation list (the incidentList
 * sibling of {@link CameraListBody}). When N≥2 ACT incidents stack on one screen
 * pixel — too tight to click individually on the map — the rail shows them here,
 * each individually selectable; picking one opens its normal incident body (the
 * ref IS a valid Selection, so `onSelectIncident` just dispatches `setSelected`).
 */
function IncidentListBody({
  incidents,
  onSelectIncident,
}: {
  incidents: ColocatedIncidentRef[];
  onSelectIncident?: (ref: ColocatedIncidentRef) => void;
}): JSX.Element {
  return (
    <div className="px-4 py-4 space-y-3" data-testid="incident-list-body">
      <div>
        <h2 className="text-base font-semibold">{incidents.length} incidents here</h2>
        <p className="text-xs text-slate-400 mt-0.5">
          These incidents share one spot — too close to separate on the map. Pick one to
          open it.
        </p>
      </div>
      <ul className="space-y-1.5" data-testid="incident-list">
        {incidents.map((ref) => {
          const { label, sub, rl } = incidentRowSummary(ref);
          return (
            <li key={`${ref.kind}:${ref.feature.properties.id}`}>
              <button
                type="button"
                data-testid="incident-list-item"
                onClick={() => onSelectIncident?.(ref)}
                disabled={!onSelectIncident}
                className="flex w-full items-center gap-2 rounded border border-slate-700 bg-slate-800 px-3 py-2 text-left text-sm hover:bg-slate-700 disabled:cursor-default"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{label}</span>
                  {sub ? (
                    <span className="block truncate text-[11px] text-slate-400">{sub}</span>
                  ) : null}
                </span>
                {rl != null ? (
                  // #613 — co-located raw hazards (hotspot/CAP/ice/flood) carry a machine-derived
                  // level → the Threat axis; "TL{n}" not "RL{n}" (RL = the reserved §5 acronym).
                  <span
                    className="shrink-0 rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-semibold text-slate-100"
                    title={`Threat Level ${rl}`}
                  >
                    TL{rl}
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function CameraBody({
  camera,
  pinned = false,
  onTogglePin,
  onViewshedOnSelectChange,
  viewshedOnSelect = true,
}: {
  camera: CameraFeature;
  /** #196 — pinned to the operations panel. */
  pinned?: boolean;
  /** #196 — pin/unpin from the rail (origin of the pin action). */
  onTogglePin?: () => void;
  /** #178/#262 — flip the per-operator viewshed-on-select pref. */
  onViewshedOnSelectChange?: (on: boolean) => void;
  /** #178/#262 — whether viewshed-on-select is ON (drapes on selection). */
  viewshedOnSelect?: boolean;
}): JSX.Element {
  const lastUpdated = camera.properties.last_snapshot_at
    ? formatObserved(camera.properties.last_snapshot_at)
    : '—';

  // #273 (PART XI Ruling 5.2) — wall curation from the DESK (the wall is
  // read-only). Featuring a camera adds it to the wall's ambient pool, which
  // the reel draws featured-first. Optimistic toggle → audited server mutation.
  const [wallFeatured, setWallFeatured] = useState<boolean>(
    camera.properties.wall_featured === true,
  );
  const [wallFeaturedBusy, setWallFeaturedBusy] = useState(false);
  useEffect(() => {
    setWallFeatured(camera.properties.wall_featured === true);
  }, [camera.id, camera.properties.wall_featured]);
  const toggleWallFeatured = async (): Promise<void> => {
    if (wallFeaturedBusy) return;
    const next = !wallFeatured;
    setWallFeatured(next);
    setWallFeaturedBusy(true);
    try {
      const res = await fetch(`/api/cameras/${camera.id}/wall-featured`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ featured: next, session_id: getSessionId() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Honest confirmation (#382): "Feature" is an AMBIENT curation toggle — the
      // reel seats it when no live alert needs the seat — so the toast must NOT
      // over-claim "on the wall now" (that immediacy belongs to "Send to wall",
      // the separate escalate path). Naming the deferred behaviour fixes the
      // "does nothing" perception even when seating is deferred.
      showToast(
        next
          ? 'Featured on the wall — shown when no live alert needs the seat'
          : 'Removed from the wall’s featured pool',
        { variant: 'success' },
      );
    } catch {
      setWallFeatured(!next);
      showToast('Could not update wall curation', { variant: 'error' });
    } finally {
      setWallFeaturedBusy(false);
    }
  };

  return (
    <div className="px-4 py-4 space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-base font-semibold">{camera.properties.name}</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            {camera.properties.source} · {camera.properties.external_id}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            {camera.geometry.coordinates[1].toFixed(4)}°N,{' '}
            {camera.geometry.coordinates[0].toFixed(4)}°E
          </p>
        </div>
        {onTogglePin && (
          <button
            type="button"
            data-testid="camera-pin-btn"
            aria-pressed={pinned}
            onClick={onTogglePin}
            title={pinned ? 'Unpin from operations panel' : 'Pin to operations panel'}
            className={`shrink-0 rounded border px-2 py-1 text-[11px] ${
              pinned
                ? 'border-amber-500 bg-amber-700/60 text-amber-100 hover:bg-amber-700'
                : 'border-slate-600 bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {pinned ? '📌 Pinned' : '📌 Pin'}
          </button>
        )}
      </div>
      <CameraMedia camera={camera} />
      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">Details</h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">Stream type</dt>
          <dd className="font-mono">{camera.properties.stream_type}</dd>
          <dt className="text-slate-400">Heading</dt>
          <dd
            className={
              camera.properties.is_ptz && camera.properties.stream_type === 'snapshot'
                ? 'text-slate-300 text-xs'
                : camera.properties.heading_known
                  ? 'font-mono'
                  : 'text-slate-300'
            }
          >
            {camera.properties.is_ptz && camera.properties.stream_type === 'snapshot' ? (
              // PTZ snapshot cams rotate — the stored aim is momentary, not a
              // viewshed. Say so rather than implying a fixed heading (Q.2.5.3).
              <span data-testid="ptz-rotation-note">
                PTZ — rotates 360° on cadence. Last observed aim:{' '}
                {camera.properties.heading_deg.toFixed(0)}° / FOV{' '}
                {camera.properties.fov_deg.toFixed(0)}°
              </span>
            ) : camera.properties.heading_known ? (
              <>
                {camera.properties.heading_deg.toFixed(0)}°
                {camera.properties.heading_source === 'ai_inference' &&
                  camera.properties.heading_confidence !== null && (
                    <span
                      data-testid="ai-inferred-badge"
                      className="ml-2 text-[10px] uppercase tracking-wider text-sky-400 font-sans"
                      title="Compass heading inferred from the snapshot by Claude vision"
                    >
                      AI-inferred · {Math.round((camera.properties.heading_confidence ?? 0) * 100)}%
                    </span>
                  )}
              </>
            ) : (
              'unknown (omnidirectional coverage shown)'
            )}
          </dd>
          <dt className="text-slate-400">FOV</dt>
          <dd className="font-mono">
            {camera.properties.fov_deg.toFixed(0)}°
            {camera.properties.fov_source === 'ai_inference' &&
              camera.properties.fov_confidence !== null && (
                <span
                  data-testid="fov-ai-badge"
                  className="ml-2 text-[10px] uppercase tracking-wider text-sky-400 font-sans"
                  title="FOV inferred from snapshot + satellite cross-ref"
                >
                  AI · {Math.round((camera.properties.fov_confidence ?? 0) * 100)}%
                </span>
              )}
          </dd>
          <dt className="text-slate-400">Range</dt>
          <dd className="font-mono">{camera.properties.range_m.toFixed(0)} m</dd>
          <dt className="text-slate-400">Status</dt>
          <dd>
            <StatusTag camera={camera} />
            {camera.properties.is_ptz && (
              <span className="ml-1 inline-block text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
                PTZ
              </span>
            )}
            {camera.properties.smoke_priority && (
              <span className="ml-1 inline-block text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">
                smoke priority
              </span>
            )}
          </dd>
          {camera.properties.province && (
            <>
              <dt className="text-slate-400">Province</dt>
              <dd className="font-mono">{camera.properties.province}</dd>
            </>
          )}
          <dt className="text-slate-400">Access</dt>
          <dd>
            <PermissionTag status={camera.properties.permission_status} />
          </dd>
          <dt className="text-slate-400">Last updated</dt>
          <dd className="font-mono text-xs">{lastUpdated}</dd>
        </dl>
      </section>
      {/* #178/#262 — the viewshed drapes automatically on selection (founder
          ruling: it is part of what a camera IS). This affordance is the
          per-operator "hide viewshed" opt-out; it persists across selections
          and sessions via operator prefs. Honest default ON. */}
      {onViewshedOnSelectChange && (
        <div className="space-y-1">
          <button
            type="button"
            data-testid="camera-viewshed-btn"
            aria-pressed={viewshedOnSelect}
            onClick={() => onViewshedOnSelectChange(!viewshedOnSelect)}
            className={`block w-full text-center text-sm py-1.5 rounded border ${
              viewshedOnSelect
                ? 'border-slate-700 bg-slate-800 text-teal-300 hover:bg-slate-700'
                : 'border-teal-500 bg-teal-700/50 text-teal-100 hover:bg-teal-700'
            }`}
          >
            {viewshedOnSelect ? '👁 Hide viewshed' : '👁 Show viewshed on select'}
          </button>
          {viewshedOnSelect ? (
            <p className="text-[10px] leading-tight text-slate-500">
              Showing what this camera sees: modelled terrain line-of-sight (over
              ridges) — terrain only, excludes vegetation/buildings; assumes a ~15 m
              mount. Not a survey product. Hiding applies to all cameras and persists.
            </p>
          ) : (
            <p className="text-[10px] leading-tight text-slate-500">
              Viewshed hidden — selecting a camera will not drape its line-of-sight
              until re-enabled.
            </p>
          )}
        </div>
      )}
      {/* #273 (PART XI Ruling 5.2) — wall curation. The wall is read-only, so
          set/unset the wall_featured flag here at the desk; the wall reel draws
          AMBIENT seats featured-first on its next poll. Audited verb pair. */}
      <div className="space-y-1">
        <button
          type="button"
          data-testid="camera-wall-feature-btn"
          aria-pressed={wallFeatured}
          disabled={wallFeaturedBusy}
          onClick={() => void toggleWallFeatured()}
          className={`block w-full text-center text-sm py-1.5 rounded border disabled:opacity-60 ${
            wallFeatured
              ? 'border-indigo-500 bg-indigo-700/50 text-indigo-100 hover:bg-indigo-700'
              : 'border-slate-700 bg-slate-800 text-indigo-300 hover:bg-slate-700'
          }`}
        >
          {wallFeatured ? '🖥 Featured on wall' : '🖥 Feature on wall'}
        </button>
        <p className="text-[10px] leading-tight text-slate-500">
          {wallFeatured
            ? 'In the wall’s curated ambient pool — the reel draws it featured-first when no alert demands the seat.'
            : 'Add this camera to the wall’s curated ambient pool (vistas, lookouts, municipal feeds).'}
        </p>
      </div>
      {camera.properties.viewer_url && (
        <a
          href={camera.properties.viewer_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-center text-sm py-1.5 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-sky-300"
        >
          Open viewer page ↗
        </a>
      )}
    </div>
  );
}

/** Honest catalog-status words (capitalized) for the non-serving health states.
 *  The two SERVING classes ('live') are classified by the #256 axes instead. */
const CAMERA_STATUS_LABEL: Record<string, string> = {
  live: 'Live',
  snapshot: 'Snapshot',
  offline: 'Offline',
  error: 'Error',
  degraded: 'Degraded',
  unknown: 'Unknown',
};

/**
 * The chip label + colour tone for a camera, honest about what the operator
 * will actually see (#266). The raw health `status` mislabels the two SERVING
 * classes the #256 axes distinguish — an mp4-clip cam (`media_format`
 * 'mp4_clip', `stream_type` 'snapshot') and a fresh-frame / view-time resolver
 * (`status` 'live' with no live `stream_url`) both read "live" though neither
 * is live VIDEO. When the camera is serving (`status` 'live') we classify what
 * it actually serves (Live · Clip · Snapshot); otherwise the catalog health
 * word is already honest (Offline / Degraded / …). Presentation-only — the
 * `status` field is untouched (it still gates the player mount).
 */
function honestCameraStatus(props: CameraFeature['properties']): { label: string; tone: string } {
  if (props.status !== 'live') {
    return { label: CAMERA_STATUS_LABEL[props.status] ?? props.status, tone: props.status };
  }
  switch (classifyWallMedia(props as unknown as WallMediaProps)) {
    case 'clip':
      return { label: 'Clip', tone: 'snapshot' };
    case 'live':
      return { label: 'Live', tone: 'live' };
    default:
      // 'snapshot' — a fresh-frame / view-time resolver: genuinely fresh but
      // not a live feed, so it reads SNAPSHOT, not LIVE.
      return { label: 'Snapshot', tone: 'snapshot' };
  }
}

function StatusTag({ camera }: { camera: CameraFeature }): JSX.Element {
  const { label, tone } = honestCameraStatus(camera.properties);
  const colors: Record<string, string> = {
    live: 'bg-emerald-500/20 text-emerald-300',
    snapshot: 'bg-sky-500/20 text-sky-300',
    offline: 'bg-rose-500/20 text-rose-300',
    error: 'bg-rose-500/20 text-rose-300',
    degraded: 'bg-amber-500/20 text-amber-300',
    unknown: 'bg-slate-700 text-slate-300',
  };
  const cls = colors[tone] ?? 'bg-slate-700 text-slate-300';
  return (
    <span
      data-testid="camera-status-tag"
      className={`inline-block text-[10px] px-1.5 py-0.5 rounded ${cls}`}
    >
      {label}
    </span>
  );
}

function PermissionTag({ status }: { status: string }): JSX.Element {
  const labels: Record<string, string> = {
    public_open: 'public / open data',
    attribution_required: 'attribution required',
    partnership_required: 'partnership required',
    partner_authorized: 'partner authorized',
    unknown: 'unknown',
  };
  const colors: Record<string, string> = {
    public_open: 'bg-emerald-500/20 text-emerald-300',
    attribution_required: 'bg-amber-500/20 text-amber-300',
    partnership_required: 'bg-rose-500/20 text-rose-300',
    // Display granted (founder), but not an open license → sky, between
    // emerald (open) and rose (rights not secured).
    partner_authorized: 'bg-sky-500/20 text-sky-300',
    unknown: 'bg-slate-700 text-slate-300',
  };
  return (
    <span
      className={`inline-block text-[10px] px-1.5 py-0.5 rounded ${colors[status] ?? 'bg-slate-700'}`}
    >
      {labels[status] ?? status}
    </span>
  );
}

/**
 * R.4.2 — side-panel body for a staging area (clicked on the map). Shows the
 * incident linkage, capacities, the count of resources currently staged here, and
 * a Close control (soft-delete on demobilization). Ambient logistics context.
 */
function StagingAreaBody({
  area,
  onChanged,
}: {
  area: StagingArea;
  onChanged?: () => void;
}): JSX.Element {
  const [closing, setClosing] = useState(false);
  const [closed, setClosed] = useState(Boolean(area.closed_at));

  const close = (): void => {
    if (closing) return;
    setClosing(true);
    void closeStagingArea(area.id, { session_id: getSessionId() })
      .then(() => {
        setClosed(true);
        showToast(`Staging area "${area.name}" closed.`, { variant: 'success' });
        onChanged?.();
      })
      .catch(() => showToast('Could not close staging area.', { variant: 'error' }))
      .finally(() => setClosing(false));
  };

  return (
    <div className="px-4 py-3 space-y-3 text-sm" data-testid="staging-area-body">
      <div>
        <div className="text-base font-semibold text-amber-200">{area.name}</div>
        <div className="text-[11px] text-slate-400">
          Staging Area{area.incident_number ? ` · Incident ${area.incident_number}` : ''}
          {closed ? ' · CLOSED' : ''}
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        <dt className="text-slate-500">Resources staged</dt>
        <dd className="text-slate-200" data-testid="staging-area-count">
          {area.assigned_request_count ?? 0}
        </dd>
        {area.capacity_assets != null && (
          <>
            <dt className="text-slate-500">Asset capacity</dt>
            <dd className="text-slate-200">{area.capacity_assets}</dd>
          </>
        )}
        {area.capacity_personnel != null && (
          <>
            <dt className="text-slate-500">Personnel capacity</dt>
            <dd className="text-slate-200">{area.capacity_personnel}</dd>
          </>
        )}
      </dl>
      {area.notes && <p className="text-[11px] text-slate-300">{area.notes}</p>}
      {!closed && (
        <button
          type="button"
          data-testid="staging-area-close-btn"
          disabled={closing}
          onClick={close}
          className="text-[11px] py-1 px-2 rounded bg-rose-700/60 hover:bg-rose-700 border border-rose-600 text-rose-100 disabled:opacity-50"
        >
          Close staging area
        </button>
      )}
      <p className="text-[10px] text-slate-500 leading-relaxed">
        IMS staging area (App A #81 — managed by the Operations Section). Resources transition through it on the way to{' '}
        <em>committed</em>; closed on demobilization.
      </p>
    </div>
  );
}

export function SidePanel({
  selected,
  loadingCameras,
  loadingSensors,
  isCameraPinned,
  onTogglePinCamera,
  onViewshedOnSelectChange,
  viewshedOnSelect,
  onSelectCamera,
  onSelectIncident,
  onSelectAsset,
  onSelectAircraft,
  incidentPoint,
  incidentContextId,
  onSelectMission,
  onSmokeResolved,
  onIceResolved,
  onFloodResolved,
  onMediaSignalResolved,
  onCommunityChanged,
  onMoveCommunity,
  onRecordEvacOrder,
  onOperatorEvacOrderChanged,
  onCorrectEvacOrderGeometry,
  onEnsureOperatorEvacLayerVisible,
  onCommunityEntityChanged,
  onCommunityRetired,
  onFlyToIncident,
  onOpenEvacuation,
  onFlyToCommunity,
  onViewAsCommunity,
  onOpenFusedHotspot,
  onStagingAreaChanged,
  onSectorChanged,
  onEditSectorGeometry,
  onDrawSector,
  onOpenSitrep,
  onOpenConnectedAssist,
  onFlythrough,
  flythroughActive = false,
  onOpenReplay,
  aarReplayActive = false,
  escalationIncidentId = null,
}: SidePanelProps): JSX.Element {
  const loadingBits = [
    loadingCameras ? 'cameras' : null,
    loadingSensors ? 'stations' : null,
  ].filter(Boolean);
  // #222 escalate-to-wall: derive the wall target for the current selection (null
  // for non-escalatable furniture) and whether it's the one currently on the wall.
  const escalation = useWallEscalation();
  const wallTarget = selectionToWallTarget(selected);
  const onWall =
    !!wallTarget &&
    !!escalation &&
    escalation.id === wallTarget.id &&
    escalation.kind === wallTarget.kind;
  const toggleWall = (): void => {
    if (!wallTarget) return;
    if (onWall) {
      clearWallEscalation();
      showToast('Cleared from wall');
    } else {
      // #428 — enrich an incident target with the resolved tracked incidents.id so
      // the wall can render THAT incident's sectors (sectors FK to incidents.id, not
      // the raw source id the pin keys by). Absent/unresolved → the wall shows no
      // sectors (honest empty). Never enriches a camera target.
      const target: WallTarget =
        wallTarget.kind === 'incident' && escalationIncidentId
          ? { ...wallTarget, incidentId: escalationIncidentId }
          : wallTarget;
      escalateToWall(target);
      showToast(`Sent to wall · ${wallTarget.label}`);
    }
  };
  return (
    <aside
      data-testid="side-panel"
      data-mode={selected ? 'open' : 'closed'}
      // Region law (council Part IV): at 2xl+ (≥1536px) the panel is the third
      // in-flow region. Below that it becomes a slide-over — absolute, off the
      // right edge, dropping in below the command bar only when something is
      // selected so the queue rail stays visible and the docked command bar keeps
      // its width during surge triage. (Council said <1280; reconciled up to the
      // 2xl breakpoint because the command bar needs the map-region width — see
      // build.md #130 note.)
      className={`flex flex-col w-[var(--panel-w)] max-w-[88vw] border-l border-slate-700 bg-slate-900/95 text-slate-100 overflow-y-auto
        absolute right-0 top-[var(--command-bar-h)] z-20 h-[calc(100%-var(--command-bar-h))] shadow-2xl transition-transform duration-200
        2xl:static 2xl:h-auto 2xl:shrink-0 2xl:shadow-none 2xl:translate-x-0
        ${selected ? 'translate-x-0' : 'translate-x-full 2xl:translate-x-0'}`}
    >
      <header className="px-4 py-3 border-b border-slate-700">
        <h1 className="text-lg font-semibold tracking-tight">TSR Platform</h1>
        <p className="text-xs text-slate-400 mt-1">
          Common Operating Picture (COP)
          {loadingBits.length > 0 ? ` · loading ${loadingBits.join(' + ')}…` : ''}
        </p>
      </header>
      {!selected ? (
        <div className="px-4 py-6 text-sm text-slate-300">
          <p>Click a camera, response resource, or weather station to see its details.</p>
        </div>
      ) : selected.kind === 'camera' ? (
        <CameraBody
          camera={selected.feature}
          pinned={isCameraPinned?.(selected.feature.id) ?? false}
          onTogglePin={onTogglePinCamera ? () => onTogglePinCamera(selected.feature) : undefined}
          onViewshedOnSelectChange={onViewshedOnSelectChange}
          viewshedOnSelect={viewshedOnSelect ?? true}
        />
      ) : selected.kind === 'cameraList' ? (
        <CameraListBody cameras={selected.cameras} onSelectCamera={onSelectCamera} />
      ) : selected.kind === 'incidentList' ? (
        <IncidentListBody incidents={selected.incidents} onSelectIncident={onSelectIncident} />
      ) : selected.kind === 'asset' ? (
        isDispatchAsset(selected.feature) ? (
          <DispatchCapacityPanel asset={selected.feature} />
        ) : (
          <AssetMedia asset={selected.feature} />
        )
      ) : selected.kind === 'aircraft' ? (
        <AircraftMedia aircraft={selected.feature} />
      ) : selected.kind === 'fire' ? (
        <FireBody
          fire={selected.feature}
          onSelectCamera={onSelectCamera}
          onSelectAsset={onSelectAsset}
          onSelectAircraft={onSelectAircraft}
          onOpenEvacuation={onOpenEvacuation}
          onOpenSitrep={onOpenSitrep}
          onFlythrough={onFlythrough}
          flythroughActive={flythroughActive}
          onOpenReplay={onOpenReplay}
          aarReplayActive={aarReplayActive}
          onFlyToIncident={onFlyToIncident}
          onSelectMission={onSelectMission}
          onDrawSector={onDrawSector}
        />
      ) : selected.kind === 'highRiskHotspot' ? (
        <HighRiskHotspotBody
          hotspot={selected.feature}
          onSelectCamera={onSelectCamera}
          onSelectAsset={onSelectAsset}
          onSelectAircraft={onSelectAircraft}
          onOpenSitrep={onOpenSitrep}
          onOpenConnectedAssist={onOpenConnectedAssist}
          onOpenEvacuation={onOpenEvacuation}
          onFlythrough={onFlythrough}
          flythroughActive={flythroughActive}
          onOpenReplay={onOpenReplay}
          aarReplayActive={aarReplayActive}
          onFlyToIncident={onFlyToIncident}
          onSelectMission={onSelectMission}
        />
      ) : selected.kind === 'evacuation' ? (
        <EvacuationBody
          incidentId={selected.incidentId}
          onFlyToIncident={onFlyToIncident}
          onFlyToCommunity={onFlyToCommunity}
          onSelectAsset={onSelectAsset}
          onSelectAircraft={onSelectAircraft}
        />
      ) : selected.kind === 'incident' ? (
        <div className="px-4 py-3 space-y-3" data-testid="incident-detail-body">
          <FlyToIncidentButton
            lat={selected.lat}
            lon={selected.lon}
            onFlyToIncident={onFlyToIncident}
          />
          {/* Coordination re-arch Phase 1 — the source-alert context (the thread,
              forward direction): what this incident was promoted from. */}
          <IncidentSourceContext sourceRecordType={selected.sourceRecordType} />
          {/* IMS Ruling 1 — the SAME work-surface the alert detail renders, bound to
              this incident's source alert, so the operator works the incident fully
              from the Incidents tab (confirm, recommend dispatch, request a drone,
              stage resources, advance + close) WITHOUT flying to the alert. A source-
              less (manually-declared) incident shows incident-level actions only. */}
          <IncidentWorkSurface
            source={
              selected.sourceRecordType && selected.sourceRecordId
                ? {
                    recordType: selected.sourceRecordType,
                    recordId: selected.sourceRecordId,
                    lat: selected.lat,
                    lon: selected.lon,
                    aoLabel: 'this incident',
                    declareName: '',
                  }
                : null
            }
            incidentId={selected.incidentId}
            lat={selected.lat}
            lon={selected.lon}
            onSelectCamera={onSelectCamera}
            onSelectAsset={onSelectAsset}
            onSelectAircraft={onSelectAircraft}
            onOpenSitrep={onOpenSitrep}
            onOpenConnectedAssist={onOpenConnectedAssist}
            onOpenEvacuation={onOpenEvacuation}
            onOpenReplay={onOpenReplay}
            onSelectMission={onSelectMission}
            replayActive={aarReplayActive}
          />
        </div>
      ) : selected.kind === 'rawHotspot' ? (
        <RawHotspotBody hotspot={selected.feature} onOpenFusedHotspot={onOpenFusedHotspot} />
      ) : selected.kind === 'cameraSmoke' ? (
        <CameraSmokeBody detection={selected.feature} onOpenFusedHotspot={onOpenFusedHotspot} />
      ) : selected.kind === 'utilityOutage' ? (
        <UtilityOutageBody outage={selected.feature} />
      ) : selected.kind === 'roadClosure' ? (
        <RoadClosureBody closure={selected.feature} />
      ) : selected.kind === 'evacuationOrder' ? (
        <EvacuationOrderBody feature={selected.feature} />
      ) : selected.kind === 'operatorEvacOrder' ? (
        <OperatorEvacOrderBody
          feature={selected.feature}
          onChanged={onOperatorEvacOrderChanged}
          onCorrectGeometry={
            onCorrectEvacOrderGeometry
              ? () => onCorrectEvacOrderGeometry(selected.feature)
              : undefined
          }
          onEnsureVisible={onEnsureOperatorEvacLayerVisible}
        />
      ) : selected.kind === 'arcgisFeature' ? (
        <ArcgisFeatureBody feature={selected.feature} />
      ) : selected.kind === 'commsDegradationZone' ? (
        <CommsDegradationZoneBody zone={selected.feature} />
      ) : selected.kind === 'capAlert' ? (
        <ExtremeWeatherBody
          alert={selected.feature}
          onOpenConnectedAssist={onOpenConnectedAssist}
          onSelectCamera={onSelectCamera}
          onSelectAsset={onSelectAsset}
          onSelectAircraft={onSelectAircraft}
          onFlyToIncident={onFlyToIncident}
          onOpenSitrep={onOpenSitrep}
          onOpenEvacuation={onOpenEvacuation}
          onOpenReplay={onOpenReplay}
          onSelectMission={onSelectMission}
          aarReplayActive={aarReplayActive}
        />
      ) : selected.kind === 'marineVessel' ? (
        <MarineVesselBody vessel={selected.feature} />
      ) : selected.kind === 'droneCoverage' ? (
        <DroneCoverageBody
          drone={selected.feature}
          incidentPoint={incidentPoint ?? null}
          incidentId={incidentContextId ?? null}
          onSelectMission={onSelectMission}
        />
      ) : selected.kind === 'mission' ? (
        <>
          <DroneMissionDetailBody
            mission={selected.mission}
            onFlyToIncident={onFlyToIncident}
            onSelectMission={onSelectMission}
          />
          <DroneMissionThread mission={selected.mission} />
        </>
      ) : selected.kind === 'agencyTask' ? (
        <AgencyTaskDetailBody task={selected.task} viewerRole={selected.viewerRole} />
      ) : selected.kind === 'smokeDetectionAnomaly' ? (
        <SmokeAnomalyBody anomaly={selected.feature} onResolved={onSmokeResolved} />
      ) : selected.kind === 'mediaSignalAnomaly' ? (
        <MediaSignalAnomalyBody anomaly={selected.feature} onResolved={onMediaSignalResolved} />
      ) : selected.kind === 'iceStormAnomaly' ? (
        <IceStormAnomalyBody
          anomaly={selected.feature}
          onResolved={onIceResolved}
          onOpenEvacuation={onOpenEvacuation}
          onSelectCamera={onSelectCamera}
          onSelectAsset={onSelectAsset}
          onSelectAircraft={onSelectAircraft}
          onOpenSitrep={onOpenSitrep}
          onOpenConnectedAssist={onOpenConnectedAssist}
          onOpenReplay={onOpenReplay}
          onSelectMission={onSelectMission}
          aarReplayActive={aarReplayActive}
        />
      ) : selected.kind === 'floodAnomaly' ? (
        <FloodAnomalyBody
          anomaly={selected.feature}
          onResolved={onFloodResolved}
          onFlyToIncident={onFlyToIncident}
          onOpenEvacuation={onOpenEvacuation}
          onSelectCamera={onSelectCamera}
          onSelectAsset={onSelectAsset}
          onSelectAircraft={onSelectAircraft}
          onOpenSitrep={onOpenSitrep}
          onOpenConnectedAssist={onOpenConnectedAssist}
          onOpenReplay={onOpenReplay}
          onSelectMission={onSelectMission}
          aarReplayActive={aarReplayActive}
        />
      ) : selected.kind === 'hydrometricGauge' ? (
        <HydrometricGaugeBody gauge={selected.feature} />
      ) : selected.kind === 'community' ? (
        <CommunityBody
          community={selected.feature}
          onChanged={onCommunityChanged}
          onFlyToIncident={onFlyToIncident}
          onOpenEvacuation={onOpenEvacuation}
          onMoveCommunity={onMoveCommunity}
          onRecordEvacOrder={onRecordEvacOrder}
          onViewAsCommunity={onViewAsCommunity}
          onEntityChanged={onCommunityEntityChanged}
          onRetired={onCommunityRetired}
          onOpenSitrep={onOpenSitrep}
          onFlythrough={onFlythrough}
          flythroughActive={flythroughActive}
          onOpenReplay={onOpenReplay}
          aarReplayActive={aarReplayActive}
        />
      ) : selected.kind === 'stagingArea' ? (
        <StagingAreaBody area={selected.area} onChanged={onStagingAreaChanged} />
      ) : selected.kind === 'sector' ? (
        <SectorBody
          sector={selected.sector}
          onChanged={onSectorChanged}
          onEditGeometry={onEditSectorGeometry}
        />
      ) : (
        <SensorBody sensor={selected.feature} />
      )}
      {/* #222 — Send the selected incident/camera to the shared video wall. The
          wall (a separate window) frames it, surfaces its nearest feeds + pauses
          its reel, and shows an incident profile. Only shown for escalatable kinds. */}
      {wallTarget && (
        <div className="mt-auto px-4 py-3 border-t border-slate-800">
          <button
            type="button"
            data-testid="send-to-wall-btn"
            aria-pressed={onWall}
            onClick={toggleWall}
            title={onWall ? 'Clear this from the video wall' : 'Send this to the video wall'}
            className={`block w-full rounded border py-1.5 text-center text-sm ${
              onWall
                ? 'border-amber-500 bg-amber-700/50 text-amber-100 hover:bg-amber-700'
                : 'border-slate-700 bg-slate-800 text-amber-300 hover:bg-slate-700'
            }`}
          >
            {onWall ? '📺 On wall — tap to clear' : '📺 Send to wall'}
          </button>
        </div>
      )}
      <footer
        className={`px-4 py-3 border-t border-slate-700 text-[10px] text-slate-500 leading-relaxed ${
          wallTarget ? '' : 'mt-auto'
        }`}
      >
        Weather: ECCC SWOB · Cameras: Ontario MTO (511on.ca). Open Government Licence – Canada.
      </footer>
    </aside>
  );
}
